import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Skeleton,
  Switch,
  MobileSlideDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MuiTextField as TextField,
  useTheme,
  useMediaQuery,
  EditIcon,
  InfoOutlinedIcon as InfoIcon,
  PersonOffOutlinedIcon as DeleteAccountIcon,
  CheckCircleOutlineIcon as SuccessIcon,
  CloseIcon,
} from "@/ui";
import { useSearchParams, useNavigate } from "react-router-dom";
import { HiOutlineSpeakerphone as MarketingSpeakerIcon } from "react-icons/hi";
import BottomTabBar from "@/components/BottomTabBar";
import Header from "@/components/Header";
import { useAuth, useUI } from "@/shared/state/store";
import {
  DependentsService,
  OnboardingService,
  ProfileService,
  LenderService,
  SupportService,
  MarketingService,
  BudgetService,
} from "@/shared/api/services";
import type { LenderParentRecord } from "@/shared/api/services/lenderService";
import type {
  ExpenseUpdateRequest,
  ExpensesNoteUpdateRequest,
  IncomeNoteUpdateRequest,
  IncomeUpdateRequest,
} from "@/shared/api/types";
import ProfileFormDialog from "@/components/profile/ProfileFormDialog";
import personalDetailsSchema from "../../../engine/schema/onboarding/5-personal-details.json";
import dateOfBirthSchema from "../../../engine/schema/onboarding/6-date-of-birth.json";
import contactDetailsSchema from "../../../engine/schema/onboarding/7-contact-details.json";
import addressDetailsSchema from "../../../engine/schema/profile/address-details.json";
import CompanyReferralEditDialog from "@/components/profile/CompanyReferralEditDialog";
import VulnerabilityEditDialog from "@/components/profile/VulnerabilityEditDialog";
import { VULNERABILITY_ENABLED } from "@/shared/config/featureFlags";
import accomodationTypeSchema from "../../../engine/schema/income-onboarding/1-accomodation-type.json";
import employmentStatusSchema from "../../../engine/schema/income-onboarding/2-current-employment-status.json";
import budgetWithPartnerSchema from "../../../engine/schema/income-onboarding/3-budget-with-partner.json";
import dependentsDetailsSchema from "../../../engine/schema/income-onboarding/4-details-for-dependents.json";
import vehicleDetailsSchema from "../../../engine/schema/income-onboarding/5-vehicle-details.json";
import { calculateAge } from "@/shared/lib/utils/others";
import { formatDate } from "@/shared/lib/utils/formatDate";
import {
  computeConfirmBeforeClearPlan,
  type BudgetSnapshots,
  type ProfileBudgetContext,
} from "@/shared/budget/confirmBeforeClear";

// Vulnerability category value to label mapping from schema
const VULNERABILITY_CATEGORY_LABELS: Record<string, string> = {
  textColumn1: "Accessibility issues",
  coercion: "Coercion",
  cognitiveDisorder: "Cognitive disorder",
  financialCapability: "Financial capacity",
  financialDifficulty: "Financial difficulty / resilience",
  addiction: "Addiction",
  lifeCycleEvent: "Lifecycle event",
  mentalHealth: "Mental health condition",
  physicalHealth: "Physical disability and/ or physical health",
  textColumn2: "Severe / long-term illness",
  textColumn3: "Speech, language, or communication difficulties",
};

// Profile page address dialog tweaks (frontend-only):
// - Hide the 'country' field
// - Relabel 'County/State' as 'Country'
const PROFILE_ADDRESS_DETAILS_SCHEMA = (() => {
  const schema = JSON.parse(JSON.stringify(addressDetailsSchema)) as any;
  const sections: any[] = Array.isArray(schema?.sections) ? schema.sections : [];

  for (const section of sections) {
    if (!Array.isArray(section?.fields)) continue;
    for (const field of section.fields) {
      if (field?.fieldName === "state") {
        field.label = "Country";
        field.ui = {
          ...(field.ui || {}),
          placeholder: "Enter country",
        };
      }
      if (field?.fieldName === "country") {
        field.isHidden = true;
      }
    }
  }

  return schema;
})();

export interface ClearIncomeOptions {
  hideCustomerSalary?: boolean;
  hidePartnerSalary?: boolean;
  hideOtherSalary?: boolean;
  clearChildBenefit?: boolean;
  clearHousingBenefit?: boolean;
  clearChildMaintenance?: boolean;
  clearJobSeekerAllowanceContribute?: boolean;
  clearIncomeSupport?: boolean;
  clearCarerAllowance?: boolean;
  userId?: number;
}

interface ProfileField {
  label: string;
  value: string;
  editable?: boolean;
}

interface ProfileSection {
  title: string;
  fields: ProfileField[];
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { addAlert, setLoading } = useUI();

  const [searchParams, setSearchParams] = useSearchParams();

  const [prefillData, setPrefillData] = useState<Record<string, unknown>>({});
  const [customerData, setCustomerData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [debtsData, setDebtsData] = useState<Array<Record<string, unknown>>>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [lenders, setLenders] = useState<LenderParentRecord[]>([]);

  // Dialog state
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [dobDialogOpen, setDobDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [vulnerabilityDialogOpen, setVulnerabilityDialogOpen] = useState(false);
  const [accommodationDialogOpen, setAccommodationDialogOpen] = useState(false);
  const [employmentDialogOpen, setEmploymentDialogOpen] = useState(false);
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [dependentsDialogOpen, setDependentsDialogOpen] = useState(false);
  const [dependentsInitialData, setDependentsInitialData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [savingFormId, setSavingFormId] = useState<string | null>(null);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [marketingRecord, setMarketingRecord] = useState<{
    id?: number;
    lastUpdateDate?: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonError, setDeleteReasonError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  // Hard guard against rapid double-clicks before React disables the button.
  const deleteAccountRequestInFlightRef = useRef(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState<number | null>(null);
  const [accountTypeDisplay, setAccountTypeDisplay] = useState<string>("");
  const [landlineDialogOpen, setLandlineDialogOpen] = useState(false);
  const [landlineValue, setLandlineValue] = useState<string>("");
  const [landlineError, setLandlineError] = useState<string>("");
  const [isSavingLandline, setIsSavingLandline] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Confirmation dialog: used when a profile change will hide valued budget fields
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPendingAction, setConfirmPendingAction] = useState<
    null | (() => Promise<void>)
  >(null);
  const [confirmCopy, setConfirmCopy] = useState<{
    title: string;
    message: string;
  }>({
    title: "Confirm change",
    message:
      "Updating your profile status will change your recorded budget. You should review your monthly summary before sharing your budget. Do you wish to continue?",
  });

  const truncateWithEllipsis = (value: string, maxLength = 20): string => {
    if (!value) return value;
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const formatDateToYyyyMmDd = (date: Date): string | null => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const maskReferenceIfEmail = (
    reference: string | null | undefined,
    email: string | null | undefined
  ): string => {
    const ref = (reference ?? "").trim();
    const emailValue = (email ?? "").trim();
    if (!ref) return "";
    if (emailValue && ref.toLowerCase() === emailValue.toLowerCase()) {
      return "I don't have any Reference ID";
    }
    return reference ?? "";
  };

  const fetchBudgetSnapshotsFresh = async (
    customerId: number
  ): Promise<BudgetSnapshots> => {
    try {
      const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
        await Promise.all([
          BudgetService.getIncomeData(customerId, { skipCache: true }),
          BudgetService.getIncomeNotes(customerId, { skipCache: true }),
          BudgetService.getExpenseData(customerId, { skipCache: true }),
          BudgetService.getExpenseNotes(customerId, { skipCache: true }),
        ]);

      const income =
        incomeResp?.success && Array.isArray(incomeResp.data) && incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const incomeNotes =
        incomeNotesResp?.success &&
        Array.isArray(incomeNotesResp.data) &&
        incomeNotesResp.data[0]
          ? incomeNotesResp.data[0]
          : null;
      const expenses =
        expenseResp?.success && Array.isArray(expenseResp.data) && expenseResp.data[0]
          ? expenseResp.data[0]
          : null;
      const expenseNotes =
        expenseNotesResp?.success &&
        Array.isArray(expenseNotesResp.data) &&
        expenseNotesResp.data[0]
          ? expenseNotesResp.data[0]
          : null;

      return { income, incomeNotes, expenses, expenseNotes };
    } catch {
      return { income: null, incomeNotes: null, expenses: null, expenseNotes: null };
    }
  };

  const coerceNumberFromRecord = (
    obj: Record<string, unknown> | null | undefined,
    key: string
  ): number | undefined => {
    if (!obj) return undefined;
    const v = obj[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
  };

  const buildProfileBudgetContext = (
    overrides: Partial<ProfileBudgetContext> = {}
  ): ProfileBudgetContext => {
    const customerEmploymentStatus = getString(customerData, "customerEmploymentStatus");
    const partnerEmploymentStatus = getString(customerData, "partnerEmploymentStatus");
    const jointAccount = getString(customerData, "jointAccount");
    const accommodationType = getString(customerData, "housingTenure");

    const haveChildrenUnder18 = getString(customerData, "haveChildrenUnder18");
    const dependentChildrenUnder16 = coerceNumberFromRecord(
      customerData,
      "dependentChildrenUnder16"
    );
    const dependentChildrenAged16Plus = coerceNumberFromRecord(
      customerData,
      "dependentChildrenAged16Plus"
    );

    const customerDobBefore =
      getString(customerData, "debtor1DateOfBirthStr") || getString(customerData, "dateOfBirth");
    const partnerDobBefore =
      getString(customerData, "debtor2DateOfBirthStr") ||
      getString(customerData, "debtor2DateOfBirth") ||
      getString(customerData, "partnerDateOfBirth");

    const customerAge =
      customerDobBefore && customerDobBefore.trim().length > 0
        ? calculateAge(customerDobBefore)
        : undefined;
    const partnerAge =
      partnerDobBefore && partnerDobBefore.trim().length > 0
        ? calculateAge(partnerDobBefore)
        : undefined;

    return {
      customerEmploymentStatus,
      partnerEmploymentStatus,
      jointAccount,
      accommodationType,
      haveChildrenUnder18,
      ...(typeof dependentChildrenUnder16 === "number"
        ? { dependentChildrenUnder16 }
        : {}),
      ...(typeof dependentChildrenAged16Plus === "number"
        ? { dependentChildrenAged16Plus }
        : {}),
      ...(typeof customerAge === "number" ? { customerAge } : {}),
      ...(typeof partnerAge === "number" ? { partnerAge } : {}),
      ...overrides,
    };
  };

  const buildClearOptionsFromConfirmPlan = (
    plan: ReturnType<typeof computeConfirmBeforeClearPlan>
  ): {
    income: ClearIncomeOptions;
    pension: ClearPensionOptions;
    expense: ClearExpenseOptions;
  } => {
    const income: ClearIncomeOptions = {};
    const pension: ClearPensionOptions = {};
    const expense: ClearExpenseOptions = {};

    for (const f of plan.fields) {
      if (!(f.beforeVisible && !f.afterVisible && f.hasData)) continue;
      switch (f.id) {
        case "salary.takeHomeSalary":
          income.hideCustomerSalary = true;
          break;
        case "salary.partnerTakeHomeSalary":
          income.hidePartnerSalary = true;
          break;
        case "salary.otherSalarySource":
          income.hideOtherSalary = true;
          break;
        case "benefits.jobSeekerAllowanceContribute":
          income.clearJobSeekerAllowanceContribute = true;
          break;
        case "benefits.incomeSupport":
          income.clearIncomeSupport = true;
          break;
        case "benefits.carerAllowance":
          income.clearCarerAllowance = true;
          break;
        case "benefits.childBenefit":
          income.clearChildBenefit = true;
          break;
        case "benefits.housingBenefitLocalAllowance":
          income.clearHousingBenefit = true;
          break;
        case "otherSources.childMaintenanceOrSupport":
          income.clearChildMaintenance = true;
          break;
        case "pension.statePension":
          pension.clearStatePension = true;
          break;
        case "pension.pensionCredit":
          pension.clearPensionCredit = true;
          break;
        case "pension.privatePension":
          pension.clearPrivatePension = true;
          break;
        case "pension.pensionOthers":
          pension.clearPensionOthers = true;
          break;
        case "expenses.schoolUniform":
        case "expenses.clubsAndSchoolTrips":
        case "expenses.otherSchoolCosts":
          expense.clearSchoolCosts = true;
          break;
        case "expenses.pocketMoney":
          expense.clearPocketMoney = true;
          break;
        default:
          break;
      }
    }

    return { income, pension, expense };
  };

  const hasAnyIncomeClear = (opts: ClearIncomeOptions): boolean =>
    Boolean(
      opts.hideCustomerSalary ||
        opts.hidePartnerSalary ||
        opts.hideOtherSalary ||
        opts.clearChildBenefit ||
        opts.clearHousingBenefit ||
        opts.clearChildMaintenance ||
        opts.clearJobSeekerAllowanceContribute ||
        opts.clearIncomeSupport ||
        opts.clearCarerAllowance
    );

  const hasAnyPensionClear = (opts: ClearPensionOptions): boolean =>
    Boolean(
      opts.clearStatePension ||
        opts.clearPrivatePension ||
        opts.clearPensionCredit ||
        opts.clearPensionOthers
    );

  const hasAnyExpenseClear = (opts: ClearExpenseOptions): boolean =>
    Boolean(opts.clearSchoolCosts || opts.clearPocketMoney);

  // Clear income fields that will be hidden by the profile change
  const clearHiddenIncomeFields = async (
    customerId: number,
    opts: ClearIncomeOptions
  ): Promise<void> => {
    try {
      const userId = typeof opts.userId === "number" ? opts.userId : null;
      if (!userId) return;

      // If none of the flags were set, avoid unnecessary writes
      if (
        !opts.hideCustomerSalary &&
        !opts.hidePartnerSalary &&
        !opts.hideOtherSalary &&
        !opts.clearChildBenefit &&
        !opts.clearHousingBenefit &&
        !opts.clearChildMaintenance &&
        !opts.clearJobSeekerAllowanceContribute &&
        !opts.clearIncomeSupport &&
        !opts.clearCarerAllowance
      ) {
        return;
      }

      const [incomeResp, notesResp] = await Promise.all([
        BudgetService.getIncomeData(customerId, { skipCache: true }),
        BudgetService.getIncomeNotes(customerId, { skipCache: true }),
      ]);

      const baseIncome =
        incomeResp?.success && Array.isArray(incomeResp.data) && incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const baseNotes =
        notesResp?.success && Array.isArray(notesResp.data) && notesResp.data[0]
          ? notesResp.data[0]
          : null;

      if (!baseIncome) return;

      const nowIso = new Date().toISOString();
      // IMPORTANT: /income is a PUT endpoint (replace-style). Start from the
      // full GET record and only override fields we intend to clear.
      const incomeUpdate: IncomeUpdateRequest = {
        ...(baseIncome as unknown as Record<string, unknown>),
        id: baseIncome.id,
        customerId: Number(customerId),
        lastUpdatedBy: Number(userId),
        lastUpdateDate: nowIso,
      } as IncomeUpdateRequest;

      if (opts.hideCustomerSalary) {
        incomeUpdate.basicIncome = 0;
      }

      if (opts.hidePartnerSalary) {
        incomeUpdate.debtor2BasicIncome = 0;
      }

      if (opts.hideOtherSalary) {
        incomeUpdate.otherSalaryWages1 = 0;
      }

      if (opts.clearJobSeekerAllowanceContribute) {
        incomeUpdate.jobseekerAllowanceContribute = 0;
      }

      if (opts.clearIncomeSupport) {
        incomeUpdate.incomeSupport = 0;
      }

      if (opts.clearCarerAllowance) {
        incomeUpdate.carerAllowance = 0;
      }

      if (opts.clearChildBenefit) {
        incomeUpdate.childBenefit = 0;
      }

      if (opts.clearHousingBenefit) {
        incomeUpdate.housingBenefitLocalAllowance = 0;
      }

      if (opts.clearChildMaintenance) {
        incomeUpdate.maintenanceOrChildSupport = 0;
      }

      await BudgetService.updateIncomeData(incomeUpdate);

      // Clear notes too (amount + notes only; do not touch mode)
      if (baseNotes) {
        // /income-notes is also PUT-style; send full GET record + overrides.
        const notesUpdate: IncomeNoteUpdateRequest = {
          ...(baseNotes as unknown as Record<string, unknown>),
          id: baseNotes.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
        } as IncomeNoteUpdateRequest;

        if (opts.hideCustomerSalary) notesUpdate.basicIncomeNotes = "";
        if (opts.hidePartnerSalary) notesUpdate.debtor2BasicIncomeNotes = "";
        if (opts.hideOtherSalary) notesUpdate.otherSalaryWages1Notes = "";
        if (opts.clearJobSeekerAllowanceContribute)
          notesUpdate.jobseekerAllowanceContributeNotes = "";
        if (opts.clearIncomeSupport) notesUpdate.incomeSupportNotes = "";
        if (opts.clearCarerAllowance) notesUpdate.carerAllowanceNotes = "";
        if (opts.clearChildBenefit) notesUpdate.childBenefitNotes = "";
        if (opts.clearHousingBenefit)
          notesUpdate.housingBenefitLocalAllowanceNotes = "";
        if (opts.clearChildMaintenance)
          notesUpdate.maintenanceOrChildSupportNotes = "";

        await BudgetService.updateIncomeNotes(notesUpdate);
      }
    } catch {
      // Non-blocking: if clearing fails, proceed with profile update
    }
  };

  interface ClearPensionOptions {
    clearStatePension?: boolean;
    clearPrivatePension?: boolean;
    clearPensionCredit?: boolean;
    clearPensionOthers?: boolean;
    userId?: number;
  }

  const clearHiddenPensionFields = async (
    customerId: number,
    opts: ClearPensionOptions
  ): Promise<void> => {
    try {
      const userId = typeof opts.userId === "number" ? opts.userId : null;
      if (!userId) return;
      if (
        !opts.clearStatePension &&
        !opts.clearPrivatePension &&
        !opts.clearPensionCredit &&
        !opts.clearPensionOthers
      ) {
        return;
      }

      const [incomeResp, notesResp] = await Promise.all([
        BudgetService.getIncomeData(customerId, { skipCache: true }),
        BudgetService.getIncomeNotes(customerId, { skipCache: true }),
      ]);

      const baseIncome =
        incomeResp?.success && Array.isArray(incomeResp.data) && incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const baseNotes =
        notesResp?.success && Array.isArray(notesResp.data) && notesResp.data[0]
          ? notesResp.data[0]
          : null;

      // Clear amounts (do not clear mode)
      if (baseIncome) {
        const nowIso = new Date().toISOString();
        const incomePayload: IncomeUpdateRequest = {
          ...(baseIncome as unknown as Record<string, unknown>),
          id: baseIncome.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
        } as IncomeUpdateRequest;
        if (opts.clearStatePension) incomePayload.statePension = 0;
        if (opts.clearPrivatePension) incomePayload.privatePension = 0;
        if (opts.clearPensionCredit) incomePayload.pensionCredit = 0;
        if (opts.clearPensionOthers) incomePayload.pensionOthers = 0;

        await BudgetService.updateIncomeData(incomePayload);
      }

      // Clear notes (notes are stored separately; do not touch mode)
      if (baseNotes) {
        const nowIso = new Date().toISOString();
        const notesPayload: IncomeNoteUpdateRequest = {
          ...(baseNotes as unknown as Record<string, unknown>),
          id: baseNotes.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
        } as IncomeNoteUpdateRequest;
        if (opts.clearStatePension) notesPayload.statePensionNotes = "";
        if (opts.clearPrivatePension) notesPayload.privatePensionNotes = "";
        if (opts.clearPensionCredit) notesPayload.pensionCreditNotes = "";
        if (opts.clearPensionOthers) notesPayload.pensionOthersNotes = "";

        await BudgetService.updateIncomeNotes(notesPayload);
      }
    } catch {
      // Non-blocking: if clearing fails, proceed with profile update
    }
  };

  interface ClearExpenseOptions {
    clearSchoolCosts?: boolean;
    clearPocketMoney?: boolean;
    userId?: number;
  }

  // Clear expense fields (school / pocket money) that will be hidden by profile changes
  const clearHiddenExpenseFields = async (
    customerId: number,
    opts: ClearExpenseOptions
  ): Promise<void> => {
    try {
      const userId = typeof opts.userId === "number" ? opts.userId : null;
      if (!userId) return;
      if (!opts.clearSchoolCosts && !opts.clearPocketMoney) return;

      const [expenseResp, notesResp] = await Promise.all([
        BudgetService.getExpenseData(customerId, { skipCache: true }),
        BudgetService.getExpenseNotes(customerId, { skipCache: true }),
      ]);

      const baseExpense =
        expenseResp?.success && Array.isArray(expenseResp.data) && expenseResp.data[0]
          ? expenseResp.data[0]
          : null;
      const baseNotes =
        notesResp?.success && Array.isArray(notesResp.data) && notesResp.data[0]
          ? notesResp.data[0]
          : null;

      if (!baseExpense) return;

      const nowIso = new Date().toISOString();
      // /expenses is PUT-style; send full GET record + overrides.
      const expensePayload: ExpenseUpdateRequest = {
        ...(baseExpense as unknown as Record<string, unknown>),
        id: baseExpense.id,
        customerId: Number(customerId),
        lastUpdatedBy: Number(userId),
        lastUpdateDate: nowIso,
      } as ExpenseUpdateRequest;

      if (opts.clearSchoolCosts) {
        expensePayload.schoolUniform = 0;
        expensePayload.clubsAndSchoolTrips = 0;
        expensePayload.otherSchoolCosts = 0;
      }

      if (opts.clearPocketMoney) {
        expensePayload.pocketMoneyAndSchoolTrips = 0;
      }

      await BudgetService.updateExpenseData(expensePayload);

      if (baseNotes) {
        const notesPayload: ExpensesNoteUpdateRequest = {
          ...(baseNotes as unknown as Record<string, unknown>),
          id: baseNotes.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
        } as ExpensesNoteUpdateRequest;

        if (opts.clearSchoolCosts) {
          notesPayload.schoolUniformNotes = "";
          notesPayload.clubsAndSchoolTripsNotes = "";
          notesPayload.otherSchoolCostsNotes = "";
        }
        if (opts.clearPocketMoney) {
          notesPayload.pocketMoneyAndSchoolTripsNotes = "";
        }

        await BudgetService.updateExpenseNotes(notesPayload);
      }
    } catch {
      // Non-blocking: if clearing fails, proceed with profile update
    }
  };

  // Helpers
  const getStepData = (index: number): Record<string, unknown> => {
    const key = `onboarding-step-${index}`;
    const val = prefillData?.[key] as Record<string, unknown> | undefined;
    return val || {};
  };

  const getIncomeOnboardingStepData = (
    index: number
  ): Record<string, unknown> => {
    const key = `income-onboarding-step-${index}`;
    const val = prefillData?.[key] as Record<string, unknown> | undefined;
    return val || {};
  };

  const getString = (
    obj: Record<string, unknown> | null | undefined,
    key: string
  ): string => {
    if (!obj) return "";
    const v = obj[key];
    return typeof v === "string" ? v : "";
  };

  const parseCompanyReferralData = (
    customer: Record<string, unknown> | null,
    debts: Array<Record<string, unknown>>,
    lendersList: LenderParentRecord[],
    prefillStep1?: Record<string, unknown>
  ) => {
    if (!customer) return null;

    const step1 = (prefillStep1 || {}) as Record<string, unknown>;
    const coerceToString = (v: unknown): string => {
      if (typeof v === "string") return v;
      if (typeof v === "number") return String(v);
      return "";
    };

    const step1ReferMethodCompany = coerceToString(step1["referedFromCompany"]);
    const step1OtherCompanyName = coerceToString(step1["otherCompanyName"]);
    const step1ProductName = coerceToString(step1["productName"]);
    const step1AccountType = coerceToString(step1["accountType"]);
    const step1ReferenceId = coerceToString(step1["referenceId"]);
    const step1NotReferred = Boolean(
      (step1 as any)?.notReferredByCompany === true
    );
    const step1Edited = Boolean(
      step1ReferMethodCompany ||
        step1OtherCompanyName ||
        step1ProductName ||
        step1AccountType ||
        step1ReferenceId ||
        step1NotReferred
    );

    const field5TxtFld = getString(customer, "field5TxtFld");
    const heardAboutUs = getString(customer, "heardAboutUs");
    const textField2 = getString(customer, "textField2");
    const isCreditorReferral = textField2.toLowerCase() === "creditor";

    // Helper to treat placeholder-like values as empty
    const isMeaningful = (s: string): boolean => {
      if (!s) return false;
      const v = s.trim().toLowerCase();
      return v.length > 0 && v !== "-" && v !== "n/a" && v !== "none";
    };
    // Treat vulnerability tokens as non-company values to avoid cross-domain contamination
    const isVulnerabilityValue = (s: string): boolean => {
      const v = (s || "").trim().toLowerCase();
      return (
        v === "myself" ||
        v === "partyinmybudget" ||
        v === "both" ||
        v === "none" ||
        v === "y" ||
        v === "n" ||
        v === "yes" ||
        v === "no"
      );
    };

    // Helper: select the most relevant debt row for referral details.
    const selectReferralDebt = (
      rows: Array<Record<string, unknown>>,
      creditorHint: string
    ): Record<string, unknown> | null => {
      if (!Array.isArray(rows) || rows.length === 0) return null;

      const norm = (v: unknown): string =>
        typeof v === "string" ? v.trim().toLowerCase() : "";

      // Prefer debts created from the profile "referred company" flow.
      // Backwards compatible with legacy rows that used "customer_profile".
      const preferredSources = new Set(["referred", "customer_profile"]);

      const withSource = rows.filter((row) => {
        const rawSource = (row as { source?: unknown }).source;
        const src = norm(rawSource);
        return preferredSources.has(src);
      });

      // If we do not have any referral-created debts, do not fall back to arbitrary debts.
      // Returning null prevents unrelated "accountNumber" (e.g., gas arrears) from leaking in.
      if (withSource.length === 0) {
        return null;
      }

      if (creditorHint) {
        const target = creditorHint.trim().toLowerCase();
        const byCreditor = withSource.find((row) => {
          const cred = norm((row as { creditor?: unknown }).creditor);
          return cred === target;
        });
        if (byCreditor) return byCreditor;
        // If we expected a specific creditor but didn't find a match,
        // do not fall back to an unrelated referral debt – avoid leaking
        // the previous company's reference number into the new selection.
        return null;
      }

      // Otherwise choose the most recently updated referral debt
      const toTime = (v: unknown): number => {
        if (typeof v === "string") {
          const t = Date.parse(v);
          return Number.isFinite(t) ? t : 0;
        }
        return 0;
      };
      return (
        withSource.slice().sort((a, b) => {
          const ta = Math.max(
            toTime((a as any).lastUpdateDate),
            toTime((a as any).creationDate)
          );
          const tb = Math.max(
            toTime((b as any).lastUpdateDate),
            toTime((b as any).creationDate)
          );
          return tb - ta;
        })[0] ?? null
      );
    };

    // Compute the effective creditor used when the referral debt was created.
    // Mirrors `transformToDebtFormat` in `onboardingService`:
    // - OTHER_COMPANY -> otherCompanyName
    // - otherwise prefer productName, then referedFromCompany
    const effectiveCreditor = (() => {
      if (step1ReferMethodCompany === "OTHER_COMPANY") {
        return step1OtherCompanyName;
      }
      if (step1ProductName) {
        return step1ProductName;
      }
      return step1ReferMethodCompany;
    })();

    // Get reference number and account type from debts, filtered by source and creditor.
    const referralDebt = selectReferralDebt(debts, effectiveCreditor);
    const refNumber = referralDebt
      ? coerceToString(
          (referralDebt as Record<string, unknown>)["accountNumber"]
        )
      : "";
    const debtAccountType = referralDebt
      ? coerceToString((referralDebt as Record<string, unknown>)["accTypeCode"])
      : "";

    // NOTE: textField3 is "Name of the creditor if I&E is submitted by an agent from CRM"
    // It is NOT account type. Do NOT use it as account type fallback.
    // Account type should only come from:
    // 1. Referral debt's accTypeCode
    // 2. Onboarding step-1 accountType (genuine user selection from prefill API)

    // Parse field5TxtFld for company scenarios
    let companyName = "";
    let isNotReferred = false;

    // Priority: explicit step-1 flags/values from onboarding form, then legacy customer fields
    const legacyNotReferred =
      field5TxtFld === "I have not been referred by a company";

    // IMPORTANT:
    // Some legacy users have `customer.field5TxtFld` stuck on the "not referred" string.
    // When the user edits referral details via the profile dialog, the step-1 payload is
    // the source of truth for display. Only fall back to the legacy value when step-1
    // has not been edited.
    if (step1NotReferred || (!step1Edited && legacyNotReferred)) {
      isNotReferred = true;
      companyName = "Not referred by a company";
    } else if (step1ReferMethodCompany) {
      // If user selected OTHER_COMPANY in onboarding, display provided otherCompanyName
      if (
        step1ReferMethodCompany === "OTHER_COMPANY" ||
        step1ReferMethodCompany === "Can't find my referring company"
      ) {
        companyName =
          step1OtherCompanyName || "Can't find my referring company";
      } else {
        companyName = step1ReferMethodCompany;
      }
    } else if (
      !step1Edited &&
      isMeaningful(field5TxtFld) &&
      !isVulnerabilityValue(field5TxtFld)
    ) {
      companyName = field5TxtFld;
    }

    // If backend stored a child-company name as company, map back to the parent
    const originalCompanyCandidate = companyName;
    const normalizedCompanyCandidate = (originalCompanyCandidate || "")
      .trim()
      .toLowerCase();
    if (isMeaningful(originalCompanyCandidate)) {
      const parentFromChild = lendersList.find((l) => {
        const children = Array.isArray(l.childCompany) ? l.childCompany : [];
        return children.some(
          (c) => (c || "").trim().toLowerCase() === normalizedCompanyCandidate
        );
      });
      if (parentFromChild && parentFromChild.parentCompany) {
        companyName = parentFromChild.parentCompany;
      }
    }

    // Find the company in lenders list to get configuration
    const selectedCompany = lendersList.find(
      (l) =>
        l.parentCompany &&
        l.parentCompany.trim().toLowerCase() ===
          companyName.trim().toLowerCase()
    );

    // Determine what fields should be shown based on company configuration
    const isAffirmative = (v: unknown): boolean => {
      if (typeof v === "boolean") return v;
      if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        return s === "y" || s === "yes" || s === "true" || s === "1";
      }
      return false;
    };

    const hasChildCompanies =
      selectedCompany?.childCompany &&
      Array.isArray(selectedCompany.childCompany) &&
      selectedCompany.childCompany.length > 0;
    const addToDebtList =
      isAffirmative(selectedCompany?.addToDebtList) ||
      isAffirmative((selectedCompany?.raw as any)?.addToDebtList);
    const showCompanyListY =
      isAffirmative(selectedCompany?.showCompanyList) ||
      isAffirmative((selectedCompany?.raw as any)?.showCompanyList);

    // Compute final values. Prefer values from matched referral debt when available.
    // Never fall back to field3TxtFld here (it's reserved for vulnerability audience)
    // Never fall back to textField3 - it's creditor name for CRM cases, NOT account type
    let finalProductName = coerceToString(step1ProductName || "");
    const finalAccountType = coerceToString(
      debtAccountType || step1AccountType || ""
    );
    const finalReferenceNumber = coerceToString(
      refNumber ||
        (step1Edited ? step1ReferenceId || "" : step1ReferenceId || "")
    );

    // If product name is blank but companyName looked like a child-company, use it as product
    if (
      !isMeaningful(finalProductName) &&
      isMeaningful(originalCompanyCandidate)
    ) {
      const parentForOriginal = lendersList.find(
        (l) =>
          Array.isArray(l.childCompany) &&
          l.childCompany.some(
            (c) => (c || "").trim().toLowerCase() === normalizedCompanyCandidate
          )
      );
      if (parentForOriginal) {
        finalProductName = originalCompanyCandidate;
      }
    }
    const finalOtherCompanyName =
      step1ReferMethodCompany === "OTHER_COMPANY" ||
      step1ReferMethodCompany === "Can't find my referring company"
        ? step1OtherCompanyName || ""
        : "";

    // Guard: prevent showing company name as Account Type
    const normalizedCompanyLc = (companyName || "").trim().toLowerCase();
    const candidateAccountType = (finalAccountType || "").trim();
    const finalAccountTypeSafe =
      candidateAccountType &&
      candidateAccountType.toLowerCase() === normalizedCompanyLc
        ? ""
        : candidateAccountType;

    // Determine which fields to show. Show extra details ONLY for top two referral methods
    const nonEmpty = (s: string) =>
      typeof s === "string" &&
      s.trim().length > 0 &&
      !["-", "n/a", "none"].includes(s.trim().toLowerCase());
    const referralMethodValue = (heardAboutUs || "").trim().toLowerCase();
    const isCompanyReferralMethod =
      referralMethodValue === "referred by a company";
    const isAdviceReferralMethod =
      referralMethodValue === "debt charity or advice service";
    // For creditor referrals (textField2 = "Creditor"), treat as company referral
    const canShowExtraDetails =
      isCompanyReferralMethod || isAdviceReferralMethod || isCreditorReferral;

    const shouldShowCompanyName = Boolean(
      canShowExtraDetails && !isNotReferred && nonEmpty(companyName)
    );
    const shouldShowOtherCompanyName = Boolean(
      canShowExtraDetails && !isNotReferred && nonEmpty(finalOtherCompanyName)
    );
    const shouldShowProduct = Boolean(
      canShowExtraDetails && !isNotReferred && nonEmpty(finalProductName)
    );

    // Align visibility with onboarding rules:
    // - Account Type: only when OTHER_COMPANY selected OR company has addToDebtList=true
    // - Reference Number: only when OTHER_COMPANY selected OR (addToDebtList && (showCompanyList || hasChildCompanies))
    // IMPORTANT: Also require that the field actually has a value to display
    // For creditor referrals without a matching company config, hide these fields
    const hasOtherCompany = Boolean(finalOtherCompanyName);
    const companyRequiresAccountType = Boolean(addToDebtList);
    const companyRequiresReference = Boolean(
      addToDebtList && (showCompanyListY || hasChildCompanies)
    );

    const shouldShowAccountType = Boolean(
      canShowExtraDetails &&
        !isNotReferred &&
        (hasOtherCompany || companyRequiresAccountType) &&
        nonEmpty(finalAccountType)
    );
    const shouldShowReference = Boolean(
      canShowExtraDetails &&
        !isNotReferred &&
        (hasOtherCompany || companyRequiresReference) &&
        nonEmpty(finalReferenceNumber)
    );

    // For creditor referrals, show "Referred by a company" even if heardAboutUs is empty
    const referralMethodDisplay =
      heardAboutUs || (isCreditorReferral ? "Referred by a company" : "-");

    // Debug aid: log selection and resolved values
    try {
      // eslint-disable-next-line no-console
      console.debug("[Profile:ReferralCard]", {
        debtsCount: Array.isArray(debts) ? debts.length : 0,
        selectedDebt: referralDebt
          ? {
              id: (referralDebt as any)?.id,
              source: (referralDebt as any)?.source,
              creditor: (referralDebt as any)?.creditor,
              accTypeCode: (referralDebt as any)?.accTypeCode,
              accountNumber: (referralDebt as any)?.accountNumber,
            }
          : null,
        effectiveCreditor,
        finalAccountType,
        finalReferenceNumber,
        step1Edited,
        step1AccountType,
        step1ReferenceId,
        isCreditorReferral,
        companyConfig: {
          selectedCompany: selectedCompany?.parentCompany || null,
          addToDebtList,
          showCompanyListY,
          hasChildCompanies,
        },
        visibility: {
          shouldShowAccountType,
          shouldShowReference,
        },
      });
    } catch {}

    return {
      referralMethod: referralMethodDisplay,
      companyName: companyName || "-",
      productName: finalProductName || "-",
      accountType: finalAccountTypeSafe || "-",
      referenceNumber: finalReferenceNumber || "-",
      otherCompanyName: finalOtherCompanyName || "-",
      isNotReferred,
      rawField5: field5TxtFld,
      // Display flags
      canShowExtraDetails,
      shouldShowCompanyName,
      shouldShowOtherCompanyName,
      shouldShowProduct,
      shouldShowAccountType,
      shouldShowReference,
      // Creditor referral flag - when true, the Edit button should be hidden
      isCreditorReferral,
    };
  };

  const constructPrefillFromCustomer = (
    customer: Record<string, unknown>
  ): Record<string, unknown> => {
    const prefill: Record<string, unknown> = {};

    // Step 0: Referral method
    const heardAboutUs = getString(customer, "heardAboutUs");
    const textField2 = getString(customer, "textField2");
    const isCreditorReferral = textField2.toLowerCase() === "creditor";

    if (heardAboutUs) {
      prefill["onboarding-step-0"] = { referedFrom: heardAboutUs };
    } else if (isCreditorReferral) {
      // For creditor referrals, set referral method even if heardAboutUs is empty
      // This ensures the dialog opens with the correct radio button pre-selected
      prefill["onboarding-step-0"] = { referedFrom: "Referred by a company" };
    }

    // Step 1: Company referral details
    const field5TxtFld = getString(customer, "field5TxtFld");
    if (field5TxtFld) {
      const step1: Record<string, unknown> = {};

      if (field5TxtFld === "I have not been referred by a company") {
        step1.notReferredByCompany = true;
      } else {
        step1.referedFromCompany = field5TxtFld;

        // Do not derive product name from field3TxtFld (reserved for vulnerability audience)

        // NOTE: Do NOT map textField3 to accountType here!
        // textField3 is "Name of the creditor if I&E is submitted by an agent from CRM"
        // Account type should only come from:
        // 1. Referral debt's accTypeCode
        // 2. Prefill API's onboarding-step-1.accountType (genuine user selection)
      }

      prefill["onboarding-step-1"] = step1;
    }

    return prefill;
  };

  const composeAddress = (addr: Record<string, unknown>): string => {
    const formatUkPostcodeForDisplay = (value: string): string => {
      const compact = value.trim().toUpperCase().replace(/\s+/g, "");
      if (compact.length <= 3) return compact;
      return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
    };

    const rawBuildingNumber =
      getString(addr, "buildingNumber") || getString(addr, "addressLine1");
    const buildingNumber = rawBuildingNumber
      ? truncateWithEllipsis(rawBuildingNumber, 20)
      : "";
    const buildingName = getString(addr, "buildingName") || "";
    const streetName = getString(addr, "streetName") || "";

    const line1 = [buildingNumber, buildingName, streetName]
      .map((s) => (s ? String(s).trim() : ""))
      .filter(Boolean)
      .join(" ");

    const line2Parts = [
      getString(addr, "addressLine2"),
      getString(addr, "postTown") ||
        getString(addr, "city") ||
        getString(addr, "town"),
      getString(addr, "state"),
      (() => {
        const raw = getString(addr, "postcode") || getString(addr, "postCode");
        return raw ? formatUkPostcodeForDisplay(raw) : "";
      })(),
    ]
      .map((s) => (s ? String(s).trim() : ""))
      .filter(Boolean);

    const line2 = line2Parts.join(", ");
    if (line1 && line2) return `${line1}\n${line2}`;
    if (line1) return line1;
    if (line2) return line2;
    return "";
  };

  const buildAddressRecordForDisplay = (
    step6: Record<string, unknown>,
    customer: Record<string, unknown> | null
  ): Record<string, unknown> => {
    const getText = (
      obj: Record<string, unknown> | null,
      key: string
    ): string => {
      if (!obj) return "";
      const v = obj[key];
      if (typeof v === "string") return v;
      if (typeof v === "number") return String(v);
      return "";
    };

    const pick = (key: string): string => {
      const fromStep = getText(step6, key);
      if (fromStep.trim().length > 0) return fromStep;
      const fromCustomer = getText(customer, key);
      return fromCustomer;
    };

    const buildingNumber =
      pick("buildingNumber") || pick("addressLine1") || pick("BUILDING_NUMBER");
    const postCode =
      pick("postcode") || pick("postCode") || pick("POST_CODE");

    return {
      buildingNumber,
      addressLine1: buildingNumber,
      buildingName: pick("buildingName"),
      streetName: pick("streetName"),
      addressLine2: pick("addressLine2"),
      postTown: pick("postTown"),
      city: pick("city"),
      town: pick("town"),
      state: pick("state"),
      country: pick("country"),
      // Keep both variants so composeAddress can find it
      postcode: postCode,
      postCode: postCode,
    };
  };

  const composeName = (
    pd: Record<string, unknown>,
    customer: Record<string, unknown> | null
  ): string => {
    // Try to get title
    const title =
      getString(pd, "title") || getString(customer, "customerTitle");
    const titleCapitalized = title
      ? title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()
      : "";

    // Try to get first and last name from prefill data - BOTH must exist
    const first = getString(pd, "firstName");
    const last = getString(pd, "lastName");
    if (first && last) {
      const truncatedFirst = truncateWithEllipsis(first, 20);
      const truncatedLast = truncateWithEllipsis(last, 20);
      const nameParts = [
        titleCapitalized,
        truncatedFirst,
        truncatedLast,
      ].filter(Boolean);
      return nameParts.join(" ").trim();
    }

    // Try from customer data - check for separate first and last name fields (PRIORITY) - BOTH must exist
    const debtorFirst = getString(customer, "debtor1FirstName");
    const debtorLast = getString(customer, "debtor1LastName");

    if (debtorFirst && debtorLast) {
      const truncatedFirst = truncateWithEllipsis(debtorFirst, 20);
      const truncatedLast = truncateWithEllipsis(debtorLast, 20);
      const nameParts = [
        titleCapitalized,
        truncatedFirst,
        truncatedLast,
      ].filter(Boolean);
      return nameParts.join(" ").trim();
    }

    // Try debtor1Name which might contain full name (fallback)
    const debtorName = getString(customer, "debtor1Name");
    if (debtorName) {
      return titleCapitalized
        ? `${titleCapitalized} ${debtorName}`
        : debtorName;
    }

    const displayName = getString(customer, "displayName");
    if (displayName) {
      return titleCapitalized
        ? `${titleCapitalized} ${displayName}`
        : displayName;
    }

    return "";
  };

  const composeDob = (
    dobData: Record<string, unknown>,
    customer: Record<string, unknown> | null
  ): string => {
    const direct = getString(dobData, "dateOfBirth");
    if (direct) return formatDate(direct);

    const day = getString(dobData, "dobDay");
    const month = getString(dobData, "dobMonth");
    const year = getString(dobData, "dobYear");
    if (day && month && year) {
      // dobMonth may be a numeric string ("9") or a name ("September"); try both.
      const monthNum = Number(month);
      const monthIndex = Number.isFinite(monthNum)
        ? monthNum - 1
        : new Date(`${month} 1, 2000`).getMonth();
      if (Number.isFinite(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
        const dt = new Date(Number(year), monthIndex, Number(day));
        if (!Number.isNaN(dt.getTime())) {
          const dd = String(dt.getDate()).padStart(2, "0");
          const mm = String(dt.getMonth() + 1).padStart(2, "0");
          return `${dd}/${mm}/${dt.getFullYear()}`;
        }
      }
    }
    const compact = [day, month, year].filter(Boolean).join(" ");
    if (compact) return compact;

    // Try from customer data - debtor1DateOfBirthStr (e.g. "2001-09-29")
    const dobStr = customer && getString(customer, "debtor1DateOfBirthStr");
    if (dobStr) return formatDate(dobStr);

    // Try from customer data - debtor1DateOfBirth array format [2001, 9, 29]
    const dobArray = customer && customer["debtor1DateOfBirth"];
    if (Array.isArray(dobArray) && dobArray.length === 3) {
      const [year, month, day] = dobArray;
      const dd = String(day).padStart(2, "0");
      const mm = String(month).padStart(2, "0");
      return `${dd}/${mm}/${year}`;
    }

    return "";
  };

  const refreshProfile = async (): Promise<void> => {
    if (!user?.userId) return;
    try {
      setIsLoading(true);
      setLoading("profile-load", true);
      // Ensure we don't read stale cached data after dialog saves
      try {
        OnboardingService.clearCustomerCache(user.userId);
      } catch {}
      const [prefillResp, customerResp] = await Promise.all([
        OnboardingService.loadCustomerPrefillData(user.userId, {
          skipErrorCapture: true,
        }),
        OnboardingService.getCustomerByUserId(user.userId, {
          skipErrorCapture: true,
        }),
      ]);

      // API returns an array with a single customer object - extract it
      let customerDataValue: Record<string, unknown> | null = null;
      if (customerResp?.data) {
        if (Array.isArray(customerResp.data) && customerResp.data.length > 0) {
          customerDataValue = customerResp.data[0] as Record<string, unknown>;
        } else if (typeof customerResp.data === "object") {
          customerDataValue = customerResp.data as Record<string, unknown>;
        }
      }
      setCustomerData(customerDataValue);
      // Load marketing preference
      try {
        if (customerDataValue?.customerId) {
          const m = await MarketingService.get(
            customerDataValue.customerId as number,
            {
              skipErrorCapture: true,
            }
          );
          const rec = (m as any)?.data as {
            id?: number;
            lastUpdateDate?: string;
            optedOut?: "Y" | "N";
          } | null;

          // IE Hub backend semantics:
          // - No record (GET returns [])           => customer is OPTED IN
          // - Record exists with optedOut === 'Y'  => customer is OPTED OUT
          // - Record exists with optedOut === 'N'  => customer is OPTED IN
          if (rec && typeof rec.id === "number") {
            setMarketingRecord({
              id: rec.id,
              ...(rec.lastUpdateDate !== undefined && {
                lastUpdateDate: rec.lastUpdateDate,
              }),
            });
          } else {
            setMarketingRecord(null);
          }

          const isOptedOut = (rec?.optedOut ?? "N") === "Y";
          setMarketingConsent(!isOptedOut);
        }
      } catch {}

      // Start with prefill data from API if available
      let finalPrefillData: Record<string, unknown> = {};
      if (prefillResp && prefillResp.success && prefillResp.data) {
        finalPrefillData = { ...(prefillResp.data as Record<string, unknown>) };
      }

      // Always merge/augment with customer data for company referral fields
      // This ensures onboarding-step-0 and onboarding-step-1 are populated even if prefill API doesn't have them
      if (customerDataValue) {
        const constructedPrefill =
          constructPrefillFromCustomer(customerDataValue);

        // Merge step-0 (referral method) preferring API prefill over constructed
        if (constructedPrefill["onboarding-step-0"]) {
          finalPrefillData["onboarding-step-0"] = {
            ...((constructedPrefill["onboarding-step-0"] as Record<
              string,
              unknown
            >) || {}),
            ...((finalPrefillData["onboarding-step-0"] as Record<
              string,
              unknown
            >) || {}),
          };
        }

        // Merge step-1 (company details) preferring API prefill over constructed
        if (constructedPrefill["onboarding-step-1"]) {
          finalPrefillData["onboarding-step-1"] = {
            ...((constructedPrefill["onboarding-step-1"] as Record<
              string,
              unknown
            >) || {}),
            ...((finalPrefillData["onboarding-step-1"] as Record<
              string,
              unknown
            >) || {}),
          };
        }
      }

      setPrefillData(finalPrefillData);

      // Fetch debts data to get reference number
      if (customerDataValue?.customerId) {
        try {
          const debts = await OnboardingService.getDebts(
            customerDataValue.customerId as number,
            { skipErrorCapture: true }
          );
          setDebtsData(Array.isArray(debts) ? debts : []);
        } catch {
          setDebtsData([]);
        }
      }
    } catch {
      // non-blocking
    } finally {
      setIsLoading(false);
      setLoading("profile-load", false);
    }
  };

  useEffect(() => {
    void refreshProfile();
  }, [user?.userId]);

  useEffect(() => {
    const dialog = searchParams.get("dialog");
    if (!dialog) return;

    switch (dialog) {
      case "employment-status":
        setEmploymentDialogOpen(true);
        break;
      case "vehicle-details":
        setVehicleDialogOpen(true);
        break;
      default:
        break;
    }

    const next = new URLSearchParams(searchParams);
    next.delete("dialog");
    next.delete("varId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load lenders list for company configuration
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await LenderService.fetchLenders({
          skipErrorCapture: true,
        });
        if (!cancelled && resp.success && Array.isArray(resp.data)) {
          setLenders(resp.data);
        }
      } catch {
        // non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Derived sections from prefill/customer
  const personalDetails: ProfileSection = useMemo(() => {
    const pd = getStepData(3);
    const dob = getStepData(4);
    const contact = getStepData(5);
    const address = getStepData(6);

    const name = composeName(pd, customerData);
    const dateOfBirth = composeDob(dob, customerData);
    const email =
      getString(contact, "emailId") || getString(customerData, "emailAddress");
    const phone =
      getString(contact, "phoneNumber") ||
      getString(customerData, "phoneNumber");
    const landline =
      getString(contact, "landlineNumber") ||
      getString(customerData, "landlineNumber");

    const addressRecord = buildAddressRecordForDisplay(address, customerData);
    const addressStr = composeAddress(addressRecord);

    // Accommodation comes from income-onboarding-step-0
    const incomeStep1 = getIncomeOnboardingStepData(1);
    const employmentStatus =
      getString(incomeStep1, "customerEmploymentStatus") ||
      getString(customerData, "customerEmploymentStatus") ||
      "";

    return {
      title: "Personal and contact details",
      fields: [
        { label: "Name", value: name || "-", editable: true },
        { label: "Date of birth", value: dateOfBirth || "-", editable: true },
        { label: "Email", value: email || "-", editable: false },
        { label: "Phone number", value: phone || "-", editable: true },
        { label: "Address", value: addressStr || "-", editable: true },
        { label: "Landline number", value: landline || "-", editable: true },
        {
          label: "Employment status",
          value: employmentStatus || "-",
          editable: true,
        },
      ],
    };
  }, [prefillData, customerData]);

  const referredCompany = useMemo(() => {
    return parseCompanyReferralData(
      customerData,
      debtsData,
      lenders,
      prefillData?.["onboarding-step-1"] as Record<string, unknown> | undefined
    );
  }, [customerData, debtsData, lenders, prefillData]);

  const referenceNumberDisplay = useMemo(() => {
    if (!referredCompany) return "-";
    const raw =
      typeof referredCompany.referenceNumber === "string"
        ? referredCompany.referenceNumber
        : "";
    const masked = maskReferenceIfEmail(raw, user?.emailAddress ?? null);
    const valueToUse = masked || raw;
    return valueToUse && valueToUse.trim().length > 0 ? valueToUse : "-";
  }, [referredCompany, user?.emailAddress]);

  // Resolve full account type label from code using lender APIs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setAccountTypeDisplay("");
        const rc = referredCompany as unknown as {
          accountType?: string;
          companyName?: string;
          shouldShowAccountType?: boolean;
        } | null;
        const code = (rc?.accountType || "").trim();
        if (!rc || !rc.shouldShowAccountType || !code || code === "-") return;

        const company = (rc.companyName || "").trim();
        const lc = (s: string) => s.trim().toLowerCase();
        const matches = (opt: any) => {
          const codeStr = String(opt?.code ?? "").trim();
          const labelStr = String(opt?.label ?? "").trim();
          const target = lc(code);
          return lc(codeStr) === target || lc(labelStr) === target;
        };

        let label = "";
        if (company && company !== "-") {
          try {
            const resp = await LenderService.fetchAccountTypesByCompany(
              company,
              { skipErrorCapture: true }
            );
            const list: any[] = (resp && (resp as any).data) || [];
            const hit = Array.isArray(list) ? list.find(matches) : null;
            if (hit && hit.label) label = String(hit.label);
          } catch {}
        }
        if (!label) {
          try {
            const respAll = await LenderService.fetchAllAccountTypes({
              skipErrorCapture: true,
            });
            const list: any[] = (respAll && (respAll as any).data) || [];
            const hit = Array.isArray(list) ? list.find(matches) : null;
            if (hit && hit.label) label = String(hit.label);
          } catch {}
        }
        // Guard: prevent showing company name as the resolved label
        const companyLc = company.trim().toLowerCase();
        const safeLabel =
          label && label.trim().toLowerCase() === companyLc ? "" : label;
        if (!cancelled) setAccountTypeDisplay(safeLabel || "");
      } catch {
        if (!cancelled) setAccountTypeDisplay("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [referredCompany]);

  const vehicles = useMemo(() => {
    // Vehicles comes from income-onboarding-step-4
    const incomeStep4 = getIncomeOnboardingStepData(4);
    // Robust haveVehicle parsing
    const rawHaveVehicle = incomeStep4["haveVehicle"];
    let haveVehicle: boolean | undefined = undefined;
    if (typeof rawHaveVehicle === "boolean") {
      haveVehicle = rawHaveVehicle;
    } else if (typeof rawHaveVehicle === "string") {
      const hv = rawHaveVehicle.trim().toLowerCase();
      haveVehicle = hv === "y" || hv === "yes" || hv === "true" || hv === "1";
    } else {
      const apiValue = getString(customerData, "haveVehicle");
      haveVehicle = apiValue === "Y";
    }

    // Robust vehicle count parsing
    const rawNum = incomeStep4["noOfVehiclesInHouseholds"];
    let numVehicles: number | undefined = undefined;
    if (typeof rawNum === "number") {
      numVehicles = rawNum;
    } else if (typeof rawNum === "string") {
      const parsed = parseInt(rawNum, 10);
      if (!Number.isNaN(parsed)) numVehicles = parsed;
    } else if (typeof customerData?.["noOfVehiclesInHouseholds"] === "number") {
      numVehicles = customerData["noOfVehiclesInHouseholds"] as number;
    } else if (typeof customerData?.["noOfVehiclesInHouseholds"] === "string") {
      const parsed = parseInt(
        customerData["noOfVehiclesInHouseholds"] as unknown as string,
        10
      );
      if (!Number.isNaN(parsed)) numVehicles = parsed;
    } else if (typeof customerData?.["vehicles"] === "number") {
      numVehicles = customerData["vehicles"] as number;
    } else if (typeof customerData?.["vehicles"] === "string") {
      const parsed = parseInt(
        customerData["vehicles"] as unknown as string,
        10
      );
      if (!Number.isNaN(parsed)) numVehicles = parsed;
    }

    // Clamp to [0, 5]
    if (typeof numVehicles === "number") {
      numVehicles = Math.min(Math.max(numVehicles, 0), 5);
    }

    const vehicleCount = ((): string => {
      if (typeof haveVehicle === "boolean") {
        if (!haveVehicle) return "0";
        if (typeof numVehicles === "number") return String(numVehicles);
      }
      if (typeof numVehicles === "number") return String(numVehicles);
      return "";
    })();
    return {
      title: "Vehicles",
      count: vehicleCount || "-",
    };
  }, [prefillData, customerData]);

  const propertyAndPossessions: ProfileSection = useMemo(() => {
    const incomeStep0 = getIncomeOnboardingStepData(0);
    const accommodation =
      getString(incomeStep0, "accomodationType") ||
      getString(customerData, "housingTenure") ||
      "";
    return {
      title: "Property and possessions",
      fields: [
        {
          label: "Number of Vehicle owned",
          value: vehicles.count || "-",
          editable: true,
        },
        {
          label: "Accommodation Type",
          value: accommodation || "-",
          editable: true,
        },
      ],
    };
  }, [prefillData, customerData]);

  const childrenAndAdults = useMemo(() => {
    // Children & Adults comes from income-onboarding-step-3
    const incomeStep3 = getIncomeOnboardingStepData(3);
    const hasDependents = ((): boolean => {
      if (typeof incomeStep3["haveDependents"] === "boolean")
        return incomeStep3["haveDependents"] as boolean;
      
      // Check if there are children under 18
      const apiValue = getString(customerData, "haveChildrenUnder18");
      if (apiValue === "Y") return true;
      
      // Also check if there are other adults over 18
      // adultsOver18IncludingMyself is actually other adults excluding customer and partner
      const otherAdultsRaw = customerData?.["adultsOver18IncludingMyself"];
      
      if (typeof otherAdultsRaw === "number" && otherAdultsRaw > 0) {
        return true;
      }
      
      return false;
    })();
    if (!hasDependents) {
      return {
        title: "Household",
        fields: [
          { label: "Dependants under 16", value: "No child", editable: true },
          {
            label: "Dependants between 16 - 18",
            value: "No child",
            editable: true,
          },
          {
            label: "Dependants over 18",
            value: "No other adults",
            editable: true,
          },
        ],
      } as ProfileSection;
    }

    const dependantsUnder16 =
      typeof incomeStep3["under16Dependents"] === "number"
        ? String(incomeStep3["under16Dependents"])
        : typeof customerData?.["dependentChildrenUnder16"] === "number"
          ? String(customerData["dependentChildrenUnder16"])
          : "";

    const dependants16to18 =
      typeof incomeStep3["between16to18Dependents"] === "number"
        ? String(incomeStep3["between16to18Dependents"])
        : typeof customerData?.["dependentChildrenAged16Plus"] === "number"
          ? String(customerData["dependentChildrenAged16Plus"])
          : "";

    const dependantsOver18 = ((): string => {
      if (typeof incomeStep3["otherAdultsOver18"] === "number") {
        return String(incomeStep3["otherAdultsOver18"]);
      }
      if (typeof customerData?.["adultsOver18IncludingMyself"] === "number") {
        // adultsOver18IncludingMyself is actually other adults excluding customer and partner
        return String(customerData["adultsOver18IncludingMyself"]);
      }
      return "";
    })();

    return {
      title: "Household",
      fields: [
        {
          label: "Dependants under 16",
          value: dependantsUnder16 || "-",
          editable: true,
        },
        {
          label: "Dependants between 16 - 18",
          value: dependants16to18 || "-",
          editable: true,
        },
        {
          label: "Dependants over 18",
          value: dependantsOver18 || "-",
          editable: true,
        },
      ],
    };
  }, [prefillData, customerData]);

  const partnerDetails: ProfileSection = useMemo(() => {
    // Partner details uses income-onboarding-step-2 (3-budget-with-partner.json schema)
    const incomeStep2 = getIncomeOnboardingStepData(2);

    const completingWithPartner = ((): string => {
      const raw = incomeStep2["budgetWithPartner"];
      if (typeof raw === "boolean") return raw ? "Yes" : "No";
      const jointAccount = getString(customerData, "jointAccount");
      if (jointAccount === "Y") return "Yes";
      if (jointAccount === "N") return "No";
      return "";
    })();

    // Only show partner details if completing with partner is "Yes"
    const isCompletingWithPartner = completingWithPartner === "Yes";

    const partnerName = isCompletingWithPartner
      ? getString(incomeStep2, "name") ||
        getString(customerData, "debtor2Name") ||
        ""
      : "";

    let partnerDob = "";
    if (isCompletingWithPartner) {
      const dobField = incomeStep2["dob"];
      if (dobField instanceof Date) {
        const dd = String(dobField.getDate()).padStart(2, "0");
        const mm = String(dobField.getMonth() + 1).padStart(2, "0");
        partnerDob = `${dd}/${mm}/${dobField.getFullYear()}`;
      } else if (typeof dobField === "string") {
        partnerDob = formatDate(dobField);
      } else {
        const dobStr = getString(customerData, "debtor2DateOfBirthStr");
        if (dobStr) partnerDob = formatDate(dobStr);
      }
    }

    const partnerEmployment = isCompletingWithPartner
      ? getString(incomeStep2, "employmentStatus") ||
        getString(customerData, "partnerEmploymentStatus") ||
        ""
      : "";

    // Truncate long partner names to prevent layout squashing
    const partnerNameDisplay = partnerName
      ? truncateWithEllipsis(partnerName, 20)
      : "";

    return {
      title: "Partner details",
      fields: [
        {
          label: "Completing with partner",
          value: completingWithPartner || "-",
        },
        { label: "Partner's date of birth", value: partnerDob || "-" },
        { label: "Partner's name", value: partnerNameDisplay || "-" },
        {
          label: "Partner's employment status",
          value: partnerEmployment || "-",
        },
      ],
    };
  }, [prefillData, customerData]);

  const greetingName = useMemo(() => {
    // Get first name only for greeting
    const pd = getStepData(3);
    const rawFirstName =
      getString(pd, "firstName") || getString(customerData, "debtor1FirstName");

    // If no first name, try to extract from full name
    if (!rawFirstName) {
      const fullName = getString(customerData, "debtor1Name");
      if (fullName) {
        // Remove title if present and get first name
        const nameParts = fullName.split(" ");
        const titles = ["mr", "mrs", "ms", "miss", "dr"];
        const firstPart = nameParts[0];
        if (
          firstPart &&
          titles.includes(firstPart.toLowerCase()) &&
          nameParts.length > 1 &&
          nameParts[1]
        ) {
          return truncateWithEllipsis(nameParts[1], 20);
        }
        return truncateWithEllipsis(firstPart || "there", 20);
      }
    }

    const effectiveFirst = rawFirstName || "there";
    return truncateWithEllipsis(effectiveFirst, 20);
  }, [prefillData, customerData]);

  const otherCircumstances = useMemo(() => {
    // Step 7: gating question (9-vulnerability-circumstances.json)
    const step7 = getStepData(7);
    const field2Value = getString(customerData, "field2TxtFld");
    const convertedField2 =
      field2Value === "Y" ? "yes" : field2Value === "N" ? "no" : field2Value;
    let hasVulnerability =
      getString(step7, "hasVulnerabilityCircumstances") || convertedField2; // 'yes' | 'no'

    // Step 8: audience (10-yes-vulnerability.json)
    const step8 = getStepData(8);
    const audience = getString(step8, "vulnerabilityCircumstancesAudience"); // 'MySelf' | 'PartyInMyBudget' | 'Both' | 'None'

    // Step 9: vulnerability circumstances selection (11-yes-vulnerability.json)
    const vulnSel = getStepData(9);
    const rawCategories = vulnSel["vulnerabilityCircumstancesDetails"];
    const categoryValues: string[] = Array.isArray(rawCategories)
      ? ((rawCategories as unknown[]).filter(
          (x) => typeof x === "string"
        ) as string[])
      : [];
    const categoryLabels = categoryValues
      .map((value) => VULNERABILITY_CATEGORY_LABELS[value] || value)
      .filter(Boolean);

    // Step 10: vulnerability consent (12-yes-vulnerability.json)
    const step10 = getStepData(10);
    const consentValue = getString(step10, "healthDataSharingConsent"); // 'Y' | 'N'
    const consentText =
      consentValue === "Y"
        ? "Consent given to IE Hub to share my vulnerability circumstances with companies I am sharing my budget with"
        : consentValue === "N"
          ? "No, I do not consent to share health-related data"
          : "";

    // Infer gating question answer from existing data (for legacy users)
    if (!hasVulnerability) {
      const audienceValue = getString(customerData, "field3TxtFld");
      const explicitlyNone =
        audienceValue && audienceValue.toLowerCase() === "none";

      if (categoryLabels.length > 0) {
        hasVulnerability = "yes";
      } else if (explicitlyNone) {
        hasVulnerability = "no";
      }
    }

    // Determine if the user truly skipped everything. Show "None" only when we have no meaningful data.
    const explicitlyNo =
      hasVulnerability && hasVulnerability.toLowerCase() === "no";
    const hasAnyData =
      (hasVulnerability && hasVulnerability.length > 0) ||
      (audience && audience.length > 0) ||
      categoryLabels.length > 0 ||
      (consentValue && consentValue.length > 0);

    // Show "None" only when unanswered. If explicitly answered 'No', surface "No" separately.
    const shouldShowNone = !hasAnyData && !explicitlyNo;

    return {
      skipped: shouldShowNone,
      explicitlyNo,
      categoriesChosen: categoryLabels,
      consent: consentText,
    };
  }, [prefillData, customerData]);

  const handleEdit = (label: string) => {
    if (label === "Name") setNameDialogOpen(true);
    if (label === "Date of birth") setDobDialogOpen(true);
    if (label === "Phone number") setContactDialogOpen(true);
    if (label === "Address") setAddressDialogOpen(true);
    if (label === "Accommodation Type") setAccommodationDialogOpen(true);
    if (label === "Number of Vehicle owned") setVehicleDialogOpen(true);
    if (label === "Landline number") {
      const initial = ((): string => {
        const contact = getStepData(5);
        return (
          getString(contact, "landlineNumber") ||
          getString(customerData, "landlineNumber") ||
          ""
        );
      })();
      setLandlineValue(initial);
      setLandlineError("");
      setLandlineDialogOpen(true);
    }
    if (label === "Employment status") setEmploymentDialogOpen(true);
    if (
      label === "Dependants under 16" ||
      label === "Dependants between 16 - 18" ||
      label === "Dependants over 18"
    ) {
      handleDependentsDialogOpen();
    }
  };

  const handleLandlineSave = async (): Promise<void> => {
    if (!user?.userId) return;
    const digitsOnly = (landlineValue || "").replace(/\D/g, "");
    if (digitsOnly && (digitsOnly.length < 10 || digitsOnly.length > 12)) {
      setLandlineError("Please enter 10-12 digits");
      return;
    }
    try {
      setIsSavingLandline(true);
      const ids = await resolveRecordIds();
      // Update via onboarding flow (same behavior as phoneNumber) so empty string clears server value
      const existingStep5 =
        (prefillData?.["onboarding-step-5"] as Record<string, unknown>) || {};
      const merged = {
        ...prefillData,
        ["onboarding-step-5"]: {
          ...existingStep5,
          landlineNumber: digitsOnly || "",
        },
      };
      await OnboardingService.updateCustomer(
        merged,
        user.userId,
        (ids.recordId as number) || (ids.customerId as number),
        (ids.customerId as number) || (ids.recordId as number),
        (user.emailAddress as string) || "",
        { skipErrorCapture: false }
      );
      setLandlineDialogOpen(false);
      addAlert({
        severity: "success",
        message: "Landline number updated",
        autoHide: true,
        duration: 1500,
      });
      await refreshProfile();
    } catch {
      addAlert({
        severity: "error",
        message: "Failed to save landline number",
        autoHide: true,
        duration: 2000,
      });
    } finally {
      setIsSavingLandline(false);
    }
  };

  const resolveRecordIds = async (): Promise<{
    recordId: number | null;
    customerId: number | null;
  }> => {
    try {
      const ls = localStorage.getItem("customerData");
      if (ls) {
        const obj = JSON.parse(ls) as Record<string, unknown>;
        const rid =
          typeof obj["id"] === "number"
            ? (obj["id"] as number)
            : typeof obj["customerId"] === "number"
              ? (obj["customerId"] as number)
              : null;
        const cid =
          typeof obj["customerId"] === "number"
            ? (obj["customerId"] as number)
            : typeof obj["id"] === "number"
              ? (obj["id"] as number)
              : null;
        if (rid && cid) return { recordId: rid, customerId: cid };
      }
    } catch {
      // ignore parse
    }
    if (user?.userId) {
      try {
        const resp = await OnboardingService.getCustomerByUserId(user.userId, {
          skipErrorCapture: true,
        });
        const data = resp?.data as Record<string, unknown> | undefined;
        if (data) {
          const rid =
            typeof data["id"] === "number"
              ? (data["id"] as number)
              : typeof data["customerId"] === "number"
                ? (data["customerId"] as number)
                : null;
          const cid =
            typeof data["customerId"] === "number"
              ? (data["customerId"] as number)
              : typeof data["id"] === "number"
                ? (data["id"] as number)
                : null;
          return { recordId: rid, customerId: cid };
        }
      } catch {
        // ignore
      }
    }
    return { recordId: null, customerId: null };
  };

  const handleNameSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    try {
      setSavingFormId("profile-name-form");
      setLoading("profile-save", true);
      const ids = await resolveRecordIds();
      // Schema returns: title, firstName, surname
      // Store both 'surname' and 'lastName' for compatibility
      const step3Data = {
        title: data.title,
        firstName: data.firstName,
        surname: data.surname,
        lastName: data.surname, // Store as lastName too for compatibility
      };
      const merged = { ...prefillData, ["onboarding-step-3"]: step3Data };
      await OnboardingService.updateCustomer(
        merged,
        user.userId,
        (ids.recordId as number) || (ids.customerId as number),
        (ids.customerId as number) || (ids.recordId as number),
        (user.emailAddress as string) || "",
        { skipErrorCapture: false }
      );
      setNameDialogOpen(false);
      addAlert({
        severity: "success",
        message: "Name updated",
        autoHide: true,
        duration: 1500,
      });
      await refreshProfile();
    } catch {
      addAlert({
        severity: "error",
        message: "Failed to save name",
        autoHide: true,
        duration: 2000,
      });
    } finally {
      setLoading("profile-save", false);
      setSavingFormId(null);
    }
  };

  const handleDobSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    const doSave = async () => {
      try {
        setSavingFormId("profile-dob-form");
        setLoading("profile-save", true);
        const ids = await resolveRecordIds();
        const merged = { ...prefillData, ["onboarding-step-4"]: data };
        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );
        setDobDialogOpen(false);
        addAlert({
          severity: "success",
          message: "Date of birth updated",
          autoHide: true,
          duration: 1500,
        });
        await refreshProfile();
      } catch {
        addAlert({
          severity: "error",
          message: "Failed to save date of birth",
          autoHide: true,
          duration: 2000,
        });
      } finally {
        setLoading("profile-save", false);
        setSavingFormId(null);
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      const newDob = data["dateOfBirth"];
      const newDobStr =
        newDob instanceof Date ? formatDateToYyyyMmDd(newDob) : null;
      const customerAgeAfter =
        typeof newDobStr === "string" ? calculateAge(newDobStr) : undefined;

      if (customerId && typeof customerAgeAfter === "number") {
        const snaps = await fetchBudgetSnapshotsFresh(customerId);
        const before = buildProfileBudgetContext();
        const after = buildProfileBudgetContext({ customerAge: customerAgeAfter });

        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId: Number(user.userId),
        });

        if (plan.needsConfirm) {
          const cid = Number(customerId);
          const uid = Number(user.userId);
          const { income, pension, expense } = buildClearOptionsFromConfirmPlan(plan);

          setConfirmCopy({
            title: "Confirm change",
            message: `This change will remove the data you entered for: ${plan.clearedLabels.join(
              ", "
            )}. Do you want to continue?`,
          });

          setConfirmPendingAction(() => async () => {
            if (hasAnyIncomeClear(income)) {
              await clearHiddenIncomeFields(cid, { ...income, userId: uid });
            }
            if (hasAnyPensionClear(pension)) {
              await clearHiddenPensionFields(cid, { ...pension, userId: uid });
            }
            if (hasAnyExpenseClear(expense)) {
              await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
            }
            await doSave();
          });
          setConfirmOpen(true);
          return;
        }
      }
    } catch {
      // If anything goes wrong during gating, fall back to saving to avoid blocking the user
    }

    await doSave();
  };

  const handleContactSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    try {
      setSavingFormId("profile-contact-form");
      setLoading("profile-save", true);
      const ids = await resolveRecordIds();
      // Schema returns: emailId, phoneNumber - store as is
      const merged = { ...prefillData, ["onboarding-step-5"]: data };
      await OnboardingService.updateCustomer(
        merged,
        user.userId,
        (ids.recordId as number) || (ids.customerId as number),
        (ids.customerId as number) || (ids.recordId as number),
        (user.emailAddress as string) || "",
        { skipErrorCapture: false }
      );
      setContactDialogOpen(false);
      addAlert({
        severity: "success",
        message: "Contact details updated",
        autoHide: true,
        duration: 1500,
      });
      await refreshProfile();
    } catch {
      addAlert({
        severity: "error",
        message: "Failed to save contact details",
        autoHide: true,
        duration: 2000,
      });
    } finally {
      setLoading("profile-save", false);
      setSavingFormId(null);
    }
  };

  const handleAddressSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    try {
      setSavingFormId("profile-address-form");
      setLoading("profile-save", true);
      const ids = await resolveRecordIds();
      // Schema returns: buildingNumber, postcode + optional full address fields (profile-only)
      // Normalize possible variants coming from the dialog
      const buildingNumberVal =
        typeof data["buildingNumber"] === "string"
          ? data["buildingNumber"]
          : typeof data["addressLine1"] === "string"
            ? data["addressLine1"]
            : "";
      const postcodeVal =
        typeof data["postcode"] === "string"
          ? data["postcode"]
          : typeof data["postCode"] === "string"
            ? data["postCode"]
            : "";

      const buildingNameVal =
        typeof data["buildingName"] === "string" ? data["buildingName"] : "";
      const streetNameVal =
        typeof data["streetName"] === "string" ? data["streetName"] : "";
      const postTownVal = typeof data["postTown"] === "string" ? data["postTown"] : "";
      const cityVal = typeof data["city"] === "string" ? data["city"] : "";
      const stateVal = typeof data["state"] === "string" ? data["state"] : "";
      const hasCountryInSubmission = Object.prototype.hasOwnProperty.call(
        data,
        "country"
      );

      // Backend mapping expects postCode with no spaces (legacy parity)
      const postCodeForBackend =
        postcodeVal && postcodeVal.trim().length > 0
          ? postcodeVal.replace(/\s+/g, "").toUpperCase()
          : "";

      const normalizeAddressPart = (v: string): string => v.trim();
      const normalizePostCode = (v: string): string =>
        v.trim().replace(/\s+/g, "").toUpperCase();

      // Compare against existing customer data to patch only changed fields.
      const prevBuildingNumber = getString(customerData, "buildingNumber") || "";
      const prevPostCode =
        getString(customerData, "postCode") ||
        getString(customerData, "postcode") ||
        "";
      const prevBuildingName = getString(customerData, "buildingName") || "";
      const prevStreetName = getString(customerData, "streetName") || "";
      const prevPostTown = getString(customerData, "postTown") || "";
      const prevCity = getString(customerData, "city") || "";
      const prevState = getString(customerData, "state") || "";
      const prevCountry = getString(customerData, "country") || "";

      // If the field is hidden (profile UX), some renderers may omit it from submitted data.
      // In that case, preserve the previous value to avoid clearing the backend field.
      const countryVal = hasCountryInSubmission
        ? typeof data["country"] === "string"
          ? data["country"]
          : ""
        : prevCountry;

      const postcodeChanged =
        normalizePostCode(prevPostCode) !== normalizePostCode(postCodeForBackend);
      const buildingNumberChanged =
        normalizeAddressPart(prevBuildingNumber) !== normalizeAddressPart(buildingNumberVal);
      const buildingNameChanged =
        normalizeAddressPart(prevBuildingName) !== normalizeAddressPart(buildingNameVal);
      const streetNameChanged =
        normalizeAddressPart(prevStreetName) !== normalizeAddressPart(streetNameVal);
      const postTownChanged =
        normalizeAddressPart(prevPostTown) !== normalizeAddressPart(postTownVal);
      const cityChanged = normalizeAddressPart(prevCity) !== normalizeAddressPart(cityVal);
      const stateChanged =
        normalizeAddressPart(prevState) !== normalizeAddressPart(stateVal);
      const countryChanged =
        normalizeAddressPart(prevCountry) !== normalizeAddressPart(countryVal);

      // Profile page: user edits address manually.
      // Do NOT call Google Places here; Google lookup is onboarding-only.
      const resolvedAddressForSave = {
        buildingNumber: buildingNumberVal,
        postCode: postCodeForBackend,
        buildingName: buildingNameVal,
        streetName: streetNameVal,
        postTown: postTownVal,
        city: cityVal,
        state: stateVal,
        country: countryVal,
      };

      const step6Data = {
        // Store for UI display (profile), even though onboarding schema only asks for 2 fields
        buildingNumber: resolvedAddressForSave.buildingNumber,
        postcode: resolvedAddressForSave.postCode,
        buildingName: resolvedAddressForSave.buildingName,
        streetName: resolvedAddressForSave.streetName,
        postTown: resolvedAddressForSave.postTown,
        city: resolvedAddressForSave.city,
        state: resolvedAddressForSave.state,
        country: resolvedAddressForSave.country,
        // keep legacy synonyms to avoid regressions elsewhere that may still read these keys
        addressLine1: resolvedAddressForSave.buildingNumber,
        postCode: resolvedAddressForSave.postCode,
      } as Record<string, unknown>;
      const merged = { ...prefillData, ["onboarding-step-6"]: step6Data };
      await OnboardingService.updateCustomer(
        merged,
        user.userId,
        (ids.recordId as number) || (ids.customerId as number),
        (ids.customerId as number) || (ids.recordId as number),
        (user.emailAddress as string) || "",
        { skipErrorCapture: false }
      );

      // Update actual customer data via ProfileService to ensure buildingNumber is stored
      // Include manual edits only (no Google enrichment on profile)
      if (ids.customerId) {
        const patchData: Record<string, unknown> = {};

        if (buildingNumberChanged) patchData.buildingNumber = resolvedAddressForSave.buildingNumber;
        if (postcodeChanged) patchData.postCode = resolvedAddressForSave.postCode;

        // Manual edits should be saved exactly (including allowing user to clear to empty string).
        if (buildingNameChanged) patchData.buildingName = resolvedAddressForSave.buildingName;
        if (streetNameChanged) patchData.streetName = resolvedAddressForSave.streetName;
        if (postTownChanged) patchData.postTown = resolvedAddressForSave.postTown;
        if (cityChanged) patchData.city = resolvedAddressForSave.city;
        if (stateChanged) patchData.state = resolvedAddressForSave.state;
        if (countryChanged) patchData.country = resolvedAddressForSave.country;

        if (Object.keys(patchData).length > 0) {
          await ProfileService.patchCustomerData(ids.customerId, patchData);
        }
      }

      setAddressDialogOpen(false);
      addAlert({
        severity: "success",
        message: "Address updated",
        autoHide: true,
        duration: 1500,
      });
      await refreshProfile();
    } catch {
      addAlert({
        severity: "error",
        message: "Failed to save address",
        autoHide: true,
        duration: 2000,
      });
    } finally {
      setLoading("profile-save", false);
      setSavingFormId(null);
    }
  };

  const handleAccommodationSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    const selected =
      typeof data["accomodationType"] === "string"
        ? (data["accomodationType"] as string)
        : "";

    const doSave = async () => {
      try {
        setSavingFormId("profile-accommodation-form");
        setLoading("profile-save", true);
        const ids = await resolveRecordIds();

        // Update income-onboarding-step-0 for prefill
        const merged = {
          ...prefillData,
          ["income-onboarding-step-0"]: { accomodationType: selected },
        };
        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );

        // Update actual customer data via ProfileService
        if (ids.customerId) {
          await ProfileService.patchCustomerData(ids.customerId, {
            housingTenure: selected,
          });
        }

        setAccommodationDialogOpen(false);
        addAlert({
          severity: "success",
          message: "Accommodation type updated",
          autoHide: true,
          duration: 1500,
        });
        await refreshProfile();
      } catch {
        addAlert({
          severity: "error",
          message: "Failed to save accommodation type",
          autoHide: true,
          duration: 2000,
        });
      } finally {
        setLoading("profile-save", false);
        setSavingFormId(null);
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (customerId && selected) {
        const incomeStep0 = getIncomeOnboardingStepData(0);
        const previousAccommodation = (() => {
          const fromStep = getString(incomeStep0, "accomodationType");
          const fromCustomer = getString(customerData, "housingTenure");
          return fromStep || fromCustomer || "";
        })();

        const isTenant = (raw: string): boolean => {
          const v = raw.trim().toLowerCase();
          if (!v) return false;
          return (
            v === "tenant - social" ||
            v === "tenant - private" ||
            v === "other" ||
            v === "others"
          );
        };

        const wasTenant = isTenant(previousAccommodation);
        const isTenantNow = isTenant(selected);

        if (wasTenant && !isTenantNow) {
          const snaps = await fetchBudgetSnapshotsFresh(customerId);
          const before = buildProfileBudgetContext({
            accommodationType: previousAccommodation,
          });
          const after = buildProfileBudgetContext({ accommodationType: selected });

          const plan = computeConfirmBeforeClearPlan({
            before,
            after,
            snaps,
            userId: Number(user.userId),
          });

          if (plan.needsConfirm) {
            const cid = Number(customerId);
            const uid = Number(user.userId);
            const { income, pension, expense } = buildClearOptionsFromConfirmPlan(plan);

            setConfirmCopy({
              title: "Confirm change",
              message: `This change will remove the data you entered for: ${plan.clearedLabels.join(
                ", "
              )}. Do you want to continue?`,
            });

            setConfirmPendingAction(() => async () => {
              if (hasAnyIncomeClear(income)) {
                await clearHiddenIncomeFields(cid, { ...income, userId: uid });
              }
              if (hasAnyPensionClear(pension)) {
                await clearHiddenPensionFields(cid, { ...pension, userId: uid });
              }
              if (hasAnyExpenseClear(expense)) {
                await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
              }
              await doSave();
            });

            setConfirmOpen(true);
            return;
          }
        }
      }
    } catch {
      // If anything goes wrong during gating, fall back to saving to avoid blocking the user
    }

    await doSave();
  };

  const handleEmploymentSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    // Schema returns 'customerEmploymentStatus'
    const selected =
      typeof data["customerEmploymentStatus"] === "string"
        ? (data["customerEmploymentStatus"] as string)
        : "";

    // Wrapper for the actual save routine (used directly or after confirmation)
    const doSave = async () => {
      try {
        setSavingFormId("profile-employment-form");
        setLoading("profile-save", true);
        const ids = await resolveRecordIds();
        // Update income-onboarding-step-1 for prefill
        const merged = {
          ...prefillData,
          ["income-onboarding-step-1"]: { customerEmploymentStatus: selected },
        };
        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );

        // Update actual customer data via ProfileService
        if (ids.customerId) {
          await ProfileService.patchCustomerData(ids.customerId, {
            customerEmploymentStatus: selected,
          });
        }

        setEmploymentDialogOpen(false);
        addAlert({
          severity: "success",
          message: "Employment status updated",
          autoHide: true,
          duration: 1500,
        });
        await refreshProfile();
      } catch {
        addAlert({
          severity: "error",
          message: "Failed to save employment status",
          autoHide: true,
          duration: 2000,
        });
      } finally {
        setLoading("profile-save", false);
        setSavingFormId(null);
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (customerId) {
        const snaps = await fetchBudgetSnapshotsFresh(customerId);
        const before = buildProfileBudgetContext();
        const after = buildProfileBudgetContext({
          customerEmploymentStatus: selected,
        });

        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId: Number(user.userId),
        });

        if (plan.needsConfirm) {
          const cid = Number(customerId);
          const uid = Number(user.userId);
          const { income, pension, expense } = buildClearOptionsFromConfirmPlan(plan);

          setConfirmCopy({
            title: "Confirm change",
            message: `This change will remove the data you entered for: ${plan.clearedLabels.join(
              ", "
            )}. Do you want to continue?`,
          });

          setConfirmPendingAction(() => async () => {
            if (hasAnyIncomeClear(income)) {
              await clearHiddenIncomeFields(cid, { ...income, userId: uid });
            }
            if (hasAnyPensionClear(pension)) {
              await clearHiddenPensionFields(cid, { ...pension, userId: uid });
            }
            if (hasAnyExpenseClear(expense)) {
              await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
            }
            await doSave();
          });
          setConfirmOpen(true);
          return;
        }
      }

      // No confirmation required; proceed directly
      await doSave();
    } catch {
      // If anything goes wrong during gating, fall back to saving to avoid blocking the user
      await doSave();
    }
  };

  const handlePartnerSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    // Schema returns: budgetWithPartner (boolean), name, dob (Date), employmentStatus
    const incomeStep2 = getIncomeOnboardingStepData(2);

    const withPartnerRaw = data["budgetWithPartner"];
    const withPartner =
      typeof withPartnerRaw === "boolean"
        ? withPartnerRaw
        : typeof withPartnerRaw === "string"
          ? withPartnerRaw.toLowerCase() === "yes"
          : false;
    const partnerName = typeof data["name"] === "string" ? data["name"] : "";

    // Store dob as Date or string based on what was received
    let partnerDob: Date | string = "";
    if (data["dob"] instanceof Date) {
      partnerDob = data["dob"];
    } else if (typeof data["dob"] === "string") {
      partnerDob = data["dob"];
    }

    const partnerEmp =
      typeof data["employmentStatus"] === "string"
        ? data["employmentStatus"]
        : "";

    const doSave = async () => {
      try {
        setSavingFormId("profile-partner-form");
        setLoading("profile-save", true);
        const ids = await resolveRecordIds();

        // When user switches to "No" for completing with a partner, clear partner
        // details from the prefill payload so the card stops showing stale data.
        const prefillPartnerName = withPartner ? partnerName : "";
        const prefillPartnerDob = withPartner ? partnerDob : "";
        const prefillPartnerEmp = withPartner ? partnerEmp : "";

        // Update income-onboarding-step-2 for prefill
        const updatedIncomeStep2 = {
          ...incomeStep2,
          budgetWithPartner: withPartner,
          name: prefillPartnerName,
          dob: prefillPartnerDob,
          employmentStatus: prefillPartnerEmp,
        };
        const merged = {
          ...prefillData,
          ["income-onboarding-step-2"]: updatedIncomeStep2,
        };
        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );

        // Update actual customer data via ProfileService
        if (ids.customerId) {
          const apiPayload: Record<string, unknown> = {
            jointAccount: withPartner ? "Y" : "N",
          };

          if (withPartner) {
            apiPayload.debtor2Name = partnerName;
            if (partnerDob) {
              const dobDate =
                partnerDob instanceof Date ? partnerDob : new Date(partnerDob);
              if (!Number.isNaN(dobDate.getTime())) {
                const year = dobDate.getFullYear();
                const month = String(dobDate.getMonth() + 1).padStart(2, "0");
                const day = String(dobDate.getDate()).padStart(2, "0");
                const dobString = `${year}-${month}-${day}`;
                // Send normalized YYYY-MM-DD string; avoid sending raw Date objects
                apiPayload.debtor2DateOfBirthStr = dobString;
                apiPayload.debtor2DateOfBirth = dobString;
              }
            }
            if (partnerEmp) apiPayload.partnerEmploymentStatus = partnerEmp;
          } else {
            // Clear partner-related fields when selecting "No" - use null to explicitly clear
            apiPayload.debtor2Name = null;
            apiPayload.debtor2DateOfBirthStr = null;
            apiPayload.debtor2DateOfBirth = null;
            apiPayload.partnerEmploymentStatus = null;
          }

          await ProfileService.patchCustomerData(ids.customerId, apiPayload);
          try {
            ProfileService.clearCache();
          } catch {
            // ignore cache clear errors
          }
        }

        setPartnerDialogOpen(false);
        addAlert({
          severity: "success",
          message: "Partner details updated",
          autoHide: true,
          duration: 1500,
        });
        await refreshProfile();
      } catch {
        addAlert({
          severity: "error",
          message: "Failed to save partner details",
          autoHide: true,
          duration: 2000,
        });
      } finally {
        setLoading("profile-save", false);
        setSavingFormId(null);
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (customerId) {
        const newJoint = withPartner ? "Y" : "N";
        const partnerDobAfterStr = (() => {
          if (!withPartner) return null;
          if (partnerDob instanceof Date) return formatDateToYyyyMmDd(partnerDob);
          if (typeof partnerDob === "string") {
            const dt = new Date(partnerDob);
            return Number.isNaN(dt.getTime()) ? null : formatDateToYyyyMmDd(dt);
          }
          return null;
        })();
        const partnerAgeAfter =
          typeof partnerDobAfterStr === "string" ? calculateAge(partnerDobAfterStr) : undefined;

        const snaps = await fetchBudgetSnapshotsFresh(customerId);
        const before = buildProfileBudgetContext();
        const after = buildProfileBudgetContext({
          jointAccount: newJoint,
          partnerEmploymentStatus: withPartner ? partnerEmp : "",
          ...(withPartner && typeof partnerAgeAfter === "number"
            ? { partnerAge: partnerAgeAfter }
            : {}),
        });

        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId: Number(user.userId),
        });

        if (plan.needsConfirm) {
          const cid = Number(customerId);
          const uid = Number(user.userId);
          const { income, pension, expense } = buildClearOptionsFromConfirmPlan(plan);

          setConfirmCopy({
            title: "Confirm change",
            message: `This change will remove the data you entered for: ${plan.clearedLabels.join(
              ", "
            )}. Do you want to continue?`,
          });

          setConfirmPendingAction(() => async () => {
            if (hasAnyIncomeClear(income)) {
              await clearHiddenIncomeFields(cid, { ...income, userId: uid });
            }
            if (hasAnyPensionClear(pension)) {
              await clearHiddenPensionFields(cid, { ...pension, userId: uid });
            }
            if (hasAnyExpenseClear(expense)) {
              await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
            }
            await doSave();
          });

          setConfirmOpen(true);
          return;
        }
      }
    } catch {
      // If anything goes wrong during gating, fall back to saving to avoid blocking the user
    }

    await doSave();
  };

  const handleVehicleSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    try {
      setSavingFormId("profile-vehicle-form");
      setLoading("profile-save", true);
      const ids = await resolveRecordIds();

      // Schema returns: haveVehicle (boolean), noOfVehiclesInHouseholds (number)
      const haveVehicle =
        typeof data["haveVehicle"] === "boolean"
          ? (data["haveVehicle"] as boolean)
          : false;
      const countRaw = data["noOfVehiclesInHouseholds"];
      const count =
        typeof countRaw === "number"
          ? countRaw
          : typeof countRaw === "string"
            ? parseInt(countRaw as string, 10)
            : 0;
      // Default to 1 when switching to Yes with empty/zero, clamp to max 5
      const vehiclesNum = haveVehicle
        ? Number.isFinite(count) && count > 0
          ? Math.min(count, 5)
          : 1
        : 0;

      // Update income-onboarding-step-4 for prefill
      const merged = {
        ...prefillData,
        ["income-onboarding-step-4"]: {
          haveVehicle: haveVehicle,
          noOfVehiclesInHouseholds: vehiclesNum,
        },
      };
      await OnboardingService.updateCustomer(
        merged,
        user.userId,
        (ids.recordId as number) || (ids.customerId as number),
        (ids.customerId as number) || (ids.recordId as number),
        (user.emailAddress as string) || "",
        { skipErrorCapture: false }
      );

      // Update actual customer data via ProfileService
      if (ids.customerId) {
        await ProfileService.patchCustomerData(ids.customerId, {
          haveVehicle: haveVehicle ? "Y" : "N",
          noOfVehiclesInHouseholds: vehiclesNum,
        });
      }

      setVehicleDialogOpen(false);
      addAlert({
        severity: "success",
        message: "Vehicle details updated",
        autoHide: true,
        duration: 1500,
      });
      await refreshProfile();
    } catch {
      addAlert({
        severity: "error",
        message: "Failed to save vehicle details",
        autoHide: true,
        duration: 2000,
      });
    } finally {
      setLoading("profile-save", false);
      setSavingFormId(null);
    }
  };

  const handleDependentsDialogOpen = async () => {
    try {
      // Data comes from income-onboarding-step-3
      const incomeStep3 = getIncomeOnboardingStepData(3);
      // Also read partner completion from income-onboarding-step-2/customer API
      const incomeStep2 = getIncomeOnboardingStepData(2);

      // Helper to safely coerce numeric-looking values to numbers
      const coerceToNumber = (value: unknown, fallback: number = 0): number => {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string") {
          const parsed = parseInt(value, 10);
          return Number.isNaN(parsed) ? fallback : parsed;
        }
        return fallback;
      };

      // counts (ensure numbers, not strings)
      const under16 =
        typeof incomeStep3["under16Dependents"] === "number"
          ? incomeStep3["under16Dependents"]
          : coerceToNumber(customerData?.["dependentChildrenUnder16"]);

      const between16to18 =
        typeof incomeStep3["between16to18Dependents"] === "number"
          ? incomeStep3["between16to18Dependents"]
          : coerceToNumber(customerData?.["dependentChildrenAged16Plus"]);

      // Calculate other adults
      // adultsOver18IncludingMyself is actually other adults excluding customer and partner
      const otherAdults = ((): number => {
        if (typeof incomeStep3["otherAdultsOver18"] === "number") {
          return incomeStep3["otherAdultsOver18"];
        }
        return coerceToNumber(customerData?.["adultsOver18IncludingMyself"]);
      })();

      // haveDependents boolean - check step data, then children, then other adults
      const raw = incomeStep3["haveDependents"];
      let haveDependents = typeof raw === "boolean" ? raw : false;
      if (!haveDependents) {
        const apiValue = getString(customerData, "haveChildrenUnder18");
        if (apiValue === "Y") {
          haveDependents = true;
        } else if (under16 > 0 || between16to18 > 0 || otherAdults > 0) {
          // Also check if there are any dependents or other adults
          haveDependents = true;
        }
      }

      // Determine with-partner flag from either step-2 or API 'jointAccount' (Y/N)
      const withPartner = ((): boolean => {
        const raw = incomeStep2["budgetWithPartner"];
        if (typeof raw === "boolean") return raw;
        const joint = getString(customerData, "jointAccount");
        return joint === "Y";
      })();

      const step3Data: Record<string, unknown> = {
        haveDependents,
        under16Dependents: under16,
        between16to18Dependents: between16to18,
        otherAdultsOver18: otherAdults,
        // Provide context for count label expression
        budgetWithPartner: withPartner,
        jointAccount:
          getString(customerData, "jointAccount") || (withPartner ? "Y" : "N"),
      };

      if (haveDependents && customerData?.customerId) {
        // Fetch dependent DOBs from the API
        try {
          const dependentsResponse = await DependentsService.getDependents(
            customerData.customerId as number
          );
          if (dependentsResponse.success && dependentsResponse.data) {
            // Map DOBs to form fields based on category - with numbered fields
            const under16Dependents = dependentsResponse.data
              .filter((dep) => dep.dependantCategory === "age-under16")
              .sort(
                (a, b) =>
                  Number(a.childDependantNo) - Number(b.childDependantNo)
              );

            const between16to18Dependents = dependentsResponse.data
              .filter((dep) => dep.dependantCategory === "age-16to18")
              .sort(
                (a, b) =>
                  Number(a.childDependantNo) - Number(b.childDependantNo)
              );

            // Set numbered DOB fields for under16 dependents
            under16Dependents.forEach((dep) => {
              const dobStr = dep.childDependantDobStr;
              if (dobStr && typeof dobStr === "string") {
                const fieldName = `under16DependentsDOB_${dep.childDependantNo}`;
                step3Data[fieldName] = new Date(dobStr);
              }
            });

            // Set numbered DOB fields for 16-18 dependents
            between16to18Dependents.forEach((dep) => {
              const dobStr = dep.childDependantDobStr;
              if (dobStr && typeof dobStr === "string") {
                const fieldName = `between16to18DependentsDOB_${dep.childDependantNo}`;
                step3Data[fieldName] = new Date(dobStr);
              }
            });
          }
        } catch (error) {
          // Failed to fetch dependent DOBs
        }
      }

      setDependentsInitialData(step3Data);
      setDependentsDialogOpen(true);
    } catch (error) {
      // Failed to prepare dependents dialog
      setDependentsDialogOpen(true);
    }
  };

  const handleDependentsSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    // Normalize payload for step-3: when haveDependents is false, force counts to 0 and strip DOBs
    const haveDeps = data?.haveDependents === true;
    const normalizedStep3: Record<string, unknown> = { ...data };
    if (!haveDeps) {
      normalizedStep3.under16Dependents = 0;
      normalizedStep3.between16to18Dependents = 0;
      normalizedStep3.otherAdultsOver18 = 0;
      // Remove any dynamically created DOB fields if present
      Object.keys(normalizedStep3).forEach((k) => {
        if (
          k.startsWith("under16DependentsDOB_") ||
          k.startsWith("between16to18DependentsDOB_")
        ) {
          delete normalizedStep3[k];
        }
      });
    }

    const computeHasChildrenFromCounts = (
      source: Record<string, unknown> | null | undefined
    ): boolean => {
      if (!source) return false;
      const under16 =
        typeof source.under16Dependents === "number"
          ? source.under16Dependents
          : 0;
      const between16to18 =
        typeof source.between16to18Dependents === "number"
          ? source.between16to18Dependents
          : 0;
      return under16 > 0 || between16to18 > 0;
    };

    const beforeStep3 = (dependentsInitialData ?? {}) as Record<
      string,
      unknown
    >;
    const beforeHasChildrenByCounts = computeHasChildrenFromCounts(beforeStep3);
    const beforeHaveChildrenUnder18 =
      getString(customerData, "haveChildrenUnder18") === "Y";

    const afterHasChildrenByCounts =
      computeHasChildrenFromCounts(normalizedStep3);
    const afterHaveDeps = normalizedStep3.haveDependents === true;
    const afterHasChildren = afterHaveDeps && afterHasChildrenByCounts;

    const willLoseChildren =
      (beforeHasChildrenByCounts || beforeHaveChildrenUnder18) &&
      !afterHasChildren;

    const doSave = async () => {
      try {
        setSavingFormId("profile-dependents-form");
        setLoading("profile-save", true);
        const ids = await resolveRecordIds();

        // Update income-onboarding-step-3 for prefill
        const merged = {
          ...prefillData,
          ["income-onboarding-step-3"]: normalizedStep3,
        };

        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );

        // Update actual customer data via ProfileService
        if (ids.customerId) {
          const apiPayload: Record<string, unknown> = {};

          if (data.haveDependents !== undefined) {
            // Check if there are actual children (not just adults over 18)
            const hasActualChildren =
              data.haveDependents === true &&
              ((typeof data.under16Dependents === "number" &&
                data.under16Dependents > 0) ||
                (typeof data.between16to18Dependents === "number" &&
                  data.between16to18Dependents > 0));
            apiPayload.haveChildrenUnder18 = hasActualChildren ? "Y" : "N";

            if (data.haveDependents === true) {
              if (data.under16Dependents !== undefined) {
                apiPayload.dependentChildrenUnder16 = data.under16Dependents;
              }
              if (data.between16to18Dependents !== undefined) {
                apiPayload.dependentChildrenAged16Plus =
                  data.between16to18Dependents;
              }
              if (data.otherAdultsOver18 !== undefined) {
                // adultsOver18IncludingMyself is actually other adults excluding customer and partner
                apiPayload.adultsOver18IncludingMyself = data.otherAdultsOver18;
              }

              // Handle dependent DOBs - save to ie-Customer-Dependent-Dob API
              if (ids.customerId && user?.userId) {
                try {
                  // Get existing dependents to compare and update
                  const existingDependentsResponse =
                    await DependentsService.getDependents(ids.customerId);
                  const existingDependents =
                    existingDependentsResponse.success &&
                    existingDependentsResponse.data
                      ? existingDependentsResponse.data
                      : [];

                  // Process under16 DOBs - loop through numbered fields
                  const under16Count =
                    typeof data.under16Dependents === "number"
                      ? data.under16Dependents
                      : 0;
                  for (let i = 1; i <= under16Count; i += 1) {
                    const dobFieldName = `under16DependentsDOB_${i}`;
                    const dobValue = data[dobFieldName];

                    // Find if this record already exists for this index
                    const existingUnder16 = existingDependents.find(
                      (dep) =>
                        dep.dependantCategory === "age-under16" &&
                        dep.childDependantNo === String(i)
                    );

                    let dobDate: Date | null = null;

                    if (dobValue instanceof Date) {
                      dobDate = dobValue;
                    } else if (
                      typeof dobValue === "string" ||
                      typeof dobValue === "number"
                    ) {
                      dobDate = new Date(dobValue);
                    }

                    if (
                      dobDate instanceof Date &&
                      !Number.isNaN(dobDate.getTime())
                    ) {
                      const year = dobDate.getFullYear();
                      const month = String(dobDate.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const day = String(dobDate.getDate()).padStart(2, "0");
                      const dobStr = `${year}-${month}-${day}`;

                      if (existingUnder16 && existingUnder16.id) {
                        // Update existing record
                        const updatePayload = {
                          id: existingUnder16.id,
                          customerId: existingUnder16.customerId,
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-under16" as const,
                          lastUpdatedBy: Number(user.userId),
                          lastUpdateDate: new Date().toISOString(),
                        };
                        await DependentsService.updateDependent(updatePayload);
                      } else {
                        // Create new record
                        await DependentsService.createDependent({
                          customerId: Number(ids.customerId),
                          userId: Number(user.userId),
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-under16",
                          createdBy: Number(user.userId),
                        });
                      }
                    } else {
                      // No valid DOB provided; if a record exists for this index, delete it
                      if (existingUnder16 && existingUnder16.id) {
                        try {
                          await DependentsService.deleteDependent(
                            ids.customerId as number,
                            existingUnder16.id
                          );
                        } catch {
                          // non-blocking
                        }
                      }
                    }
                  }

                  // Delete under16 dependents that exceed the current count
                  const deletableUnder16 = existingDependents.filter(
                    (dep) =>
                      dep.dependantCategory === "age-under16" &&
                      Number(dep.childDependantNo) > under16Count
                  );
                  for (const dep of deletableUnder16) {
                    if (dep.id) {
                      await DependentsService.deleteDependent(
                        ids.customerId as number,
                        dep.id
                      );
                    }
                  }

                  // Process 16-18 DOBs - loop through numbered fields
                  const between16to18Count =
                    typeof data.between16to18Dependents === "number"
                      ? data.between16to18Dependents
                      : 0;
                  for (let i = 1; i <= between16to18Count; i += 1) {
                    const dobFieldName = `between16to18DependentsDOB_${i}`;
                    const dobValue = data[dobFieldName];

                    // Find if this record already exists for this index
                    const existing16to18 = existingDependents.find(
                      (dep) =>
                        dep.dependantCategory === "age-16to18" &&
                        dep.childDependantNo === String(i)
                    );

                    let dobDate: Date | null = null;

                    if (dobValue instanceof Date) {
                      dobDate = dobValue;
                    } else if (
                      typeof dobValue === "string" ||
                      typeof dobValue === "number"
                    ) {
                      dobDate = new Date(dobValue);
                    }

                    if (
                      dobDate instanceof Date &&
                      !Number.isNaN(dobDate.getTime())
                    ) {
                      const year = dobDate.getFullYear();
                      const month = String(dobDate.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const day = String(dobDate.getDate()).padStart(2, "0");
                      const dobStr = `${year}-${month}-${day}`;

                      if (existing16to18 && existing16to18.id) {
                        // Update existing record
                        const updatePayload = {
                          id: existing16to18.id,
                          customerId: existing16to18.customerId,
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-16to18" as const,
                          lastUpdatedBy: Number(user.userId),
                          lastUpdateDate: new Date().toISOString(),
                        };
                        await DependentsService.updateDependent(updatePayload);
                      } else {
                        // Create new record
                        await DependentsService.createDependent({
                          customerId: Number(ids.customerId),
                          userId: Number(user.userId),
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-16to18",
                          createdBy: Number(user.userId),
                        });
                      }
                    } else {
                      // No valid DOB provided; if a record exists for this index, delete it
                      if (existing16to18 && existing16to18.id) {
                        try {
                          await DependentsService.deleteDependent(
                            ids.customerId as number,
                            existing16to18.id
                          );
                        } catch {
                          // non-blocking
                        }
                      }
                    }
                  }

                  // Delete 16-18 dependents that exceed the current count
                  const deletable16to18 = existingDependents.filter(
                    (dep) =>
                      dep.dependantCategory === "age-16to18" &&
                      Number(dep.childDependantNo) > between16to18Count
                  );
                  for (const dep of deletable16to18) {
                    if (dep.id) {
                      await DependentsService.deleteDependent(
                        ids.customerId as number,
                        dep.id
                      );
                    }
                  }
                } catch {
                  // Failed to save dependent DOBs
                  // Don't throw - allow the rest of the form to save
                }
              }
            } else {
              // When switching to No, clear counts and delete all dependent DOB records
              apiPayload.dependentChildrenUnder16 = 0;
              apiPayload.dependentChildrenAged16Plus = 0;
              apiPayload.adultsOver18IncludingMyself = 0;
              try {
                const existingDependentsResponse =
                  await DependentsService.getDependents(ids.customerId);
                const existingDependents =
                  existingDependentsResponse.success &&
                  existingDependentsResponse.data
                    ? existingDependentsResponse.data
                    : [];
                for (const dep of existingDependents) {
                  if (dep.id) {
                    await DependentsService.deleteDependent(
                      ids.customerId as number,
                      dep.id
                    );
                  }
                }
              } catch {
                // Non-blocking
              }
            }
          }

          await ProfileService.patchCustomerData(ids.customerId, apiPayload);
        }

        setDependentsDialogOpen(false);
        addAlert({
          severity: "success",
          message: "Dependents details updated",
          autoHide: true,
          duration: 1500,
        });
        await refreshProfile();
      } catch {
        addAlert({
          severity: "error",
          message: "Failed to save dependents details",
          autoHide: true,
          duration: 2000,
        });
      } finally {
        setLoading("profile-save", false);
        setSavingFormId(null);
      }
    };

    if (!willLoseChildren || !user?.userId) {
      await doSave();
      return;
    }

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (!customerId) {
        await doSave();
        return;
      }

      const snaps = await fetchBudgetSnapshotsFresh(customerId);
      const beforeUnder16 =
        coerceNumberFromRecord(beforeStep3, "under16Dependents") ??
        coerceNumberFromRecord(customerData, "dependentChildrenUnder16");
      const before16Plus =
        coerceNumberFromRecord(beforeStep3, "between16to18Dependents") ??
        coerceNumberFromRecord(customerData, "dependentChildrenAged16Plus");
      const afterUnder16 = coerceNumberFromRecord(normalizedStep3, "under16Dependents");
      const after16Plus = coerceNumberFromRecord(normalizedStep3, "between16to18Dependents");

      const before = buildProfileBudgetContext({
        haveChildrenUnder18: getString(customerData, "haveChildrenUnder18"),
        ...(typeof beforeUnder16 === "number"
          ? { dependentChildrenUnder16: beforeUnder16 }
          : {}),
        ...(typeof before16Plus === "number"
          ? { dependentChildrenAged16Plus: before16Plus }
          : {}),
      });
      const after = buildProfileBudgetContext({
        // Check if there are actual children (not just adults over 18)
        haveChildrenUnder18: afterHasChildrenByCounts ? "Y" : "N",
        ...(typeof afterUnder16 === "number"
          ? { dependentChildrenUnder16: afterUnder16 }
          : {}),
        ...(typeof after16Plus === "number"
          ? { dependentChildrenAged16Plus: after16Plus }
          : {}),
      });

      const plan = computeConfirmBeforeClearPlan({
        before,
        after,
        snaps,
        userId: Number(user.userId),
      });

      if (plan.needsConfirm) {
        const cid = Number(customerId);
        const uid = Number(user.userId);
        const { income, pension, expense } = buildClearOptionsFromConfirmPlan(plan);

        setConfirmCopy({
          title: "Confirm change",
          message: `This change will remove the data you entered for: ${plan.clearedLabels.join(
            ", "
          )}. Do you want to continue?`,
        });

        setConfirmPendingAction(() => async () => {
          if (hasAnyIncomeClear(income)) {
            await clearHiddenIncomeFields(cid, { ...income, userId: uid });
          }
          if (hasAnyPensionClear(pension)) {
            await clearHiddenPensionFields(cid, { ...pension, userId: uid });
          }
          if (hasAnyExpenseClear(expense)) {
            await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
          }
          await doSave();
        });
        setConfirmOpen(true);
        return;
      }
    } catch (err) {
      console.error("[handleDependentsSave] Confirm-before-clear gating failed:", err);
      addAlert({
        severity: "error",
        message: "Oops, something went wrong. Please try again.",
        autoHide: true,
        duration: 3000,
      });
      return;
    }

    await doSave();
  };

  const renderField = (
    field: ProfileField,
    index: number,
    isFullWidth?: boolean,
    dataPrivate: boolean = true
  ) => (
    <Box
      key={index}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minWidth: isFullWidth ? "100%" : { xs: "100%", md: "300px" },
      }}
    >
      <Typography variant="caption" sx={{ color: "#757575", fontSize: "14px" }}>
        {field.label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: field.label === "Address" ? "flex-start" : "center",
          gap: 1.5,
        }}
      >
        <Typography
          {...(dataPrivate && { 'data-private': true })}
          variant="body1"
          sx={{
            fontWeight: 600,
            color: "#111726",
            fontSize: "16px",
            ...(field.label === "Address" && { whiteSpace: "pre-line" }),
          }}
        >
          {field.value}
        </Typography>
        {field.editable && (
          <IconButton
            size="small"
            sx={{ color: "#6b7280", p: 0 }}
            onClick={() => handleEdit(field.label)}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  const renderSkeletonField = (isFullWidth?: boolean) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minWidth: isFullWidth ? "100%" : { xs: "100%", md: "300px" },
      }}
    >
      <Skeleton variant="text" width="40%" height={20} />
      <Skeleton variant="text" width="70%" height={24} />
    </Box>
  );

  return (
    <>
      <ProfileFormDialog
        open={nameDialogOpen}
        title="Edit name"
        schema={personalDetailsSchema as unknown as any}
        isSaving={savingFormId === "profile-name-form"}
        initialData={(() => {
          const pd = getStepData(3);
          // Map to schema field names: title, firstName, surname
          return {
            title:
              getString(pd, "title") ||
              getString(customerData, "customerTitle"),
            firstName:
              getString(pd, "firstName") ||
              getString(customerData, "debtor1FirstName"),
            surname:
              getString(pd, "surname") ||
              getString(pd, "lastName") ||
              getString(customerData, "debtor1LastName"),
          };
        })()}
        formId="profile-name-form"
        onSubmit={handleNameSave}
        onClose={() => setNameDialogOpen(false)}
      />
      <ProfileFormDialog
        open={dobDialogOpen}
        title="Edit date of birth"
        schema={dateOfBirthSchema as unknown as any}
        isSaving={savingFormId === "profile-dob-form"}
        initialData={(() => {
          const dob = getStepData(4);
          // Schema expects 'dateOfBirth' as Date object
          let dateValue: Date | undefined;

          // Try direct dateOfBirth field first
          const directDob = dob["dateOfBirth"];
          if (directDob instanceof Date) {
            dateValue = directDob;
          } else if (typeof directDob === "string") {
            dateValue = new Date(directDob);
          }

          // Fallback to customer data
          if (!dateValue || isNaN(dateValue.getTime())) {
            const dobStr = getString(customerData, "debtor1DateOfBirthStr");
            if (dobStr) {
              dateValue = new Date(dobStr);
            } else {
              const dobArray = customerData?.["debtor1DateOfBirth"];
              if (Array.isArray(dobArray) && dobArray.length === 3) {
                const year = dobArray[0];
                const month = dobArray[1];
                const day = dobArray[2];
                if (
                  typeof year === "number" &&
                  typeof month === "number" &&
                  typeof day === "number"
                ) {
                  dateValue = new Date(year, month - 1, day);
                }
              }
            }
          }

          return {
            dateOfBirth:
              dateValue && !isNaN(dateValue.getTime()) ? dateValue : undefined,
          };
        })()}
        formId="profile-dob-form"
        onSubmit={handleDobSave}
        onClose={() => setDobDialogOpen(false)}
      />
      <ProfileFormDialog
        open={contactDialogOpen}
        title="Edit contact details"
        schema={contactDetailsSchema as unknown as any}
        isSaving={savingFormId === "profile-contact-form"}
        initialData={(() => {
          const contact = getStepData(5);
          // Schema expects: emailId, phoneNumber
          return {
            emailId:
              getString(contact, "emailId") ||
              getString(customerData, "emailAddress"),
            phoneNumber:
              getString(contact, "phoneNumber") ||
              getString(customerData, "phoneNumber"),
          };
        })()}
        formId="profile-contact-form"
        onSubmit={handleContactSave}
        onClose={() => setContactDialogOpen(false)}
      />
      <MobileSlideDialog
        open={landlineDialogOpen}
        onClose={() => setLandlineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#003d34",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Edit landline number</span>
          <IconButton
            onClick={() => setLandlineDialogOpen(false)}
            sx={{ color: "#6b7280" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: "auto" }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: "#757575" }}>
            Please provide your home phone number here (eg. 02894562222)
          </Typography>
          <TextField
            value={landlineValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = (e.target.value || "").replace(/\D/g, "");
              setLandlineValue(val);
              if (!val) {
                setLandlineError("");
              } else if (val.length < 10 || val.length > 12) {
                setLandlineError("Please enter 10-12 digits");
              } else {
                setLandlineError("");
              }
            }}
            fullWidth
            placeholder="Eg. 02894562222"
            variant="outlined"
            margin="dense"
            error={Boolean(landlineError)}
            helperText={landlineError || "Optional; 10-12 digits"}
            inputProps={{
              maxLength: 12,
              inputMode: "numeric",
              pattern: "[0-9]*",
              "aria-label": "Landline number",
            }}
            InputProps={{
              sx: {
                "& .MuiOutlinedInput-input": { fontSize: "14px" },
                "& .MuiOutlinedInput-inputMultiline": {
                  lineHeight: 1.5,
                  padding: "12px 14px",
                },
                "& fieldset": { borderColor: "#e4e7eb" },
                "&:hover fieldset": { borderColor: "#1a5053" },
                "&.Mui-focused fieldset": {
                  borderColor: "#1a5053",
                  boxShadow: "0 0 0 2px rgba(26,80,83,0.15)",
                },
                borderRadius: "12px",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: "1px solid #e4e7eb" }}>
          <Button
            onClick={() => setLandlineDialogOpen(false)}
            sx={{
              minWidth: "140px",
              py: 1.5,
              borderColor: "#E0E0E0",
              color: "#666",
              borderRadius: 2,
              fontSize: "16px",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                borderColor: "#BDBDBD",
                backgroundColor: "#F5F5F5",
              },
              "&:disabled": {
                borderColor: "#E0E0E0",
                color: "#BDBDBD",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLandlineSave}
            variant="contained"
            sx={{
              minWidth: "140px",
              py: 1.5,
              backgroundColor: "#00717A",
              borderRadius: 2,
              fontSize: "16px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#005f66",
                boxShadow: "none",
              },
            }}
            disabled={isSavingLandline || Boolean(landlineError)}
          >
            {isSavingLandline ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </MobileSlideDialog>
      <ProfileFormDialog
        open={addressDialogOpen}
        title="Edit address details"
        schema={PROFILE_ADDRESS_DETAILS_SCHEMA as unknown as any}
        isSaving={savingFormId === "profile-address-form"}
        initialData={(() => {
          const address = getStepData(6);

          // Profile schema supports full address fields; seed from customerData first.
          const s = (v: unknown): string => {
            if (typeof v === "string") return v;
            if (typeof v === "number") return String(v);
            return "";
          };
          const formatUkPostcodeForDisplay = (value: string): string => {
            const compact = value.trim().toUpperCase().replace(/\s+/g, "");
            if (compact.length <= 3) return compact;
            return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
          };
          const buildingNumber =
            s((address as any)?.["buildingNumber"]) ||
            s((address as any)?.["addressLine1"]) ||
            s((customerData as any)?.["buildingNumber"]) ||
            s((customerData as any)?.["BUILDING_NUMBER"]) ||
            s((customerData as any)?.["addressLine1"]) ||
            s((customerData as any)?.["ADDRESSLINE1"]) ||
            "";

          const postcodeRaw =
            s((address as any)?.["postcode"]) ||
            s((address as any)?.["postCode"]) ||
            s((customerData as any)?.["postCode"]) ||
            s((customerData as any)?.["POST_CODE"]) ||
            s((customerData as any)?.["postcode"]) ||
            "";
          const postcode = postcodeRaw
            ? formatUkPostcodeForDisplay(postcodeRaw)
            : "";

          const buildingName =
            s((address as any)?.["buildingName"]) ||
            s((customerData as any)?.["buildingName"]) ||
            "";
          const streetName =
            s((address as any)?.["streetName"]) ||
            s((customerData as any)?.["streetName"]) ||
            "";
          const postTown =
            s((address as any)?.["postTown"]) ||
            s((customerData as any)?.["postTown"]) ||
            "";
          const city =
            s((address as any)?.["city"]) || s((customerData as any)?.["city"]) || "";
          const state =
            s((address as any)?.["state"]) ||
            s((customerData as any)?.["state"]) ||
            "";
          const country =
            s((address as any)?.["country"]) ||
            s((customerData as any)?.["country"]) ||
            "";

          return {
            buildingNumber,
            postcode,
            buildingName,
            streetName,
            postTown,
            city,
            state,
            country,
          };
        })()}
        formId="profile-address-form"
        onSubmit={handleAddressSave}
        onClose={() => setAddressDialogOpen(false)}
      />
      <ProfileFormDialog
        open={accommodationDialogOpen}
        title="Edit accommodation type"
        schema={accomodationTypeSchema as unknown as any}
        isSaving={savingFormId === "profile-accommodation-form"}
        initialData={(() => {
          // Schema field name is 'accomodationType' (note the typo in schema)
          // Data comes from income-onboarding-step-0
          const incomeStep0 = getIncomeOnboardingStepData(0);
          const value =
            getString(incomeStep0, "accomodationType") ||
            getString(customerData, "housingTenure") ||
            "";
          return { accomodationType: value };
        })()}
        formId="profile-accommodation-form"
        onSubmit={handleAccommodationSave}
        onClose={() => setAccommodationDialogOpen(false)}
      />
      <ProfileFormDialog
        open={employmentDialogOpen}
        title="Edit employment status"
        schema={employmentStatusSchema as unknown as any}
        isSaving={savingFormId === "profile-employment-form"}
        initialData={(() => {
          // Schema field name is 'customerEmploymentStatus'
          // Data comes from income-onboarding-step-1
          const incomeStep1 = getIncomeOnboardingStepData(1);
          const val =
            getString(incomeStep1, "customerEmploymentStatus") ||
            getString(customerData, "customerEmploymentStatus") ||
            "";
          return { customerEmploymentStatus: val };
        })()}
        formId="profile-employment-form"
        onSubmit={handleEmploymentSave}
        onClose={() => setEmploymentDialogOpen(false)}
      />
      <ProfileFormDialog
        open={partnerDialogOpen}
        title="Edit partner details"
        schema={budgetWithPartnerSchema as unknown as any}
        isSaving={savingFormId === "profile-partner-form"}
        initialData={(() => {
          // Schema field names: budgetWithPartner (boolean), name, dob (Date), employmentStatus (note typo in schema)
          // Use income-onboarding-step-2 (3-budget-with-partner.json schema)
          const incomeStep2 = getIncomeOnboardingStepData(2);

          // Get budgetWithPartner value
          const raw = incomeStep2["budgetWithPartner"];
          let withPartner = typeof raw === "boolean" ? raw : false;
          if (!withPartner) {
            const jointAccount = getString(customerData, "jointAccount");
            withPartner = jointAccount === "Y";
          }

          // If not completing with partner, return empty partner details
          if (!withPartner) {
            return {
              budgetWithPartner: false,
              name: "",
              dob: undefined,
              employmentStatus: "",
            };
          }

          // Get partner name - only if completing with partner
          const name =
            getString(incomeStep2, "name") ||
            getString(customerData, "debtor2Name") ||
            "";

          // Get partner DOB as Date object - only if completing with partner
          let dob: Date | undefined;
          const dobField = incomeStep2["dob"];
          if (dobField instanceof Date) {
            dob = dobField;
          } else if (typeof dobField === "string") {
            dob = new Date(dobField);
          } else {
            const customerDobStr = getString(
              customerData,
              "debtor2DateOfBirthStr"
            );
            if (customerDobStr) {
              dob = new Date(customerDobStr);
            }
          }

          // Get employment status (note: schema has typo 'employmentStatus') - only if completing with partner
          const emp =
            getString(incomeStep2, "employmentStatus") ||
            getString(customerData, "partnerEmploymentStatus") ||
            "";

          return {
            budgetWithPartner: withPartner,
            name,
            dob: dob && !isNaN(dob.getTime()) ? dob : undefined,
            employmentStatus: emp,
          };
        })()}
        formId="profile-partner-form"
        onSubmit={handlePartnerSave}
        onClose={() => setPartnerDialogOpen(false)}
      />
      <ProfileFormDialog
        open={vehicleDialogOpen}
        title="Edit vehicle details"
        schema={vehicleDetailsSchema as unknown as any}
        isSaving={savingFormId === "profile-vehicle-form"}
        initialData={(() => {
          // Schema field names: haveVehicle (boolean), noOfVehiclesInHouseholds (number)
          // Data comes from income-onboarding-step-4
          const incomeStep4 = getIncomeOnboardingStepData(4);

          // Get haveVehicle boolean
          let haveVehicle = false;
          const haveVehicleField = incomeStep4["haveVehicle"];
          if (typeof haveVehicleField === "boolean") {
            haveVehicle = haveVehicleField;
          } else if (typeof haveVehicleField === "string") {
            const hv = haveVehicleField.trim().toLowerCase();
            haveVehicle =
              hv === "y" || hv === "yes" || hv === "true" || hv === "1";
          } else {
            const apiValue = getString(customerData, "haveVehicle");
            haveVehicle = apiValue === "Y";
          }

          // Get vehicle count (robust to numeric strings)
          let num = 0;
          const numField = incomeStep4["noOfVehiclesInHouseholds"];
          if (typeof numField === "number") {
            num = numField;
          } else if (typeof numField === "string") {
            const parsed = parseInt(numField, 10);
            if (!Number.isNaN(parsed)) num = parsed;
          } else if (
            typeof customerData?.["noOfVehiclesInHouseholds"] === "number"
          ) {
            num = customerData["noOfVehiclesInHouseholds"] as number;
          } else if (
            typeof customerData?.["noOfVehiclesInHouseholds"] === "string"
          ) {
            const parsed = parseInt(
              customerData["noOfVehiclesInHouseholds"] as unknown as string,
              10
            );
            if (!Number.isNaN(parsed)) num = parsed;
          }
          // Clamp to [0, 5] and apply defaults per toggle
          const clamped = Math.min(Math.max(num, 0), 5);
          const finalCount = haveVehicle ? (clamped > 0 ? clamped : 1) : 0;

          return { haveVehicle, noOfVehiclesInHouseholds: finalCount };
        })()}
        formId="profile-vehicle-form"
        onSubmit={handleVehicleSave}
        onClose={() => setVehicleDialogOpen(false)}
      />
      <ProfileFormDialog
        open={dependentsDialogOpen}
        title="Edit dependents details"
        schema={dependentsDetailsSchema as unknown as any}
        isSaving={savingFormId === "profile-dependents-form"}
        initialData={dependentsInitialData || {}}
        formId="profile-dependents-form"
        onSubmit={handleDependentsSave}
        onClose={() => setDependentsDialogOpen(false)}
      />
      {/* Confirmation dialog - profile changes that hide valued budget fields */}
      <MobileSlideDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmPendingAction(null);
          // Do NOT reset confirm copy here: MobileSlideDialog uses keepMounted and
          // a slide transition, so resetting content while closing can briefly
          // show a second "generic" confirmation message.
        }}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#003d34",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{confirmCopy.title}</span>
          <IconButton
            onClick={() => {
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              // Intentionally do not reset copy (see onClose comment above).
            }}
            sx={{ color: "#6b7280" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: "auto" }}>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {confirmCopy.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: "1px solid #e4e7eb" }}>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              // Intentionally do not reset copy (see onClose comment above).
            }}
            sx={{ color: "#374151", textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            onClick={async () => {
              const fn = confirmPendingAction;
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              // Intentionally do not reset copy (see onClose comment above).
              if (typeof fn === "function") {
                await fn();
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: "#1a5053",
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#153f41" },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </MobileSlideDialog>
      <CompanyReferralEditDialog
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        onSaved={async () => {
          setCompanyDialogOpen(false);
          await refreshProfile();
        }}
        userId={user?.userId as number}
        userEmail={user?.emailAddress as string}
        prefillStep0={
          prefillData?.["onboarding-step-0"] as Record<string, unknown>
        }
        prefillStep1={
          prefillData?.["onboarding-step-1"] as Record<string, unknown>
        }
      />
      {/* Vulnerability ("Other circumstances") editor — unreachable while
          VULNERABILITY_ENABLED is false, since both edit triggers live inside the
          gated "Other circumstances" cards below. Kept mounted (renders nothing when
          closed) to keep the diff minimal and reversible. */}
      <VulnerabilityEditDialog
        open={vulnerabilityDialogOpen}
        onClose={() => setVulnerabilityDialogOpen(false)}
        onSaved={async () => {
          setVulnerabilityDialogOpen(false);
          await refreshProfile();
        }}
        userId={user?.userId as number}
        customerId={customerData?.customerId as number}
        prefillStep7={(() => {
          const step7 =
            (prefillData?.["onboarding-step-7"] as Record<string, unknown>) ||
            {};

          // If already has the answer, return as is
          if (step7.hasVulnerabilityCircumstances) {
            return step7;
          }

          // Fallback 1: Check customerData field2TxtFld (convert Y/N to yes/no)
          if (customerData?.field2TxtFld) {
            const field2Value = getString(customerData, "field2TxtFld");
            const convertedValue =
              field2Value === "Y"
                ? "yes"
                : field2Value === "N"
                  ? "no"
                  : field2Value;
            return { ...step7, hasVulnerabilityCircumstances: convertedValue };
          }

          // Fallback 2: Infer from existing data (for legacy users)
          // If they have categories chosen, they must have said "Yes"
          const step9 =
            (prefillData?.["onboarding-step-9"] as Record<string, unknown>) ||
            {};
          const categories = step9["vulnerabilityCircumstancesDetails"];
          const hasCategories =
            Array.isArray(categories) && categories.length > 0;

          // If field3TxtFld is explicitly "None", they said "No"
          const audience = getString(customerData, "field3TxtFld");
          const explicitlyNone = audience && audience.toLowerCase() === "none";

          if (hasCategories) {
            return { ...step7, hasVulnerabilityCircumstances: "yes" };
          } else if (explicitlyNone) {
            return { ...step7, hasVulnerabilityCircumstances: "no" };
          }

          return step7;
        })()}
        prefillStep8={
          prefillData?.["onboarding-step-8"] as Record<string, unknown>
        }
        prefillStep9={
          prefillData?.["onboarding-step-9"] as Record<string, unknown>
        }
        prefillStep10={
          prefillData?.["onboarding-step-10"] as Record<string, unknown>
        }
      />

      {/* Delete Account Confirmation Dialog */}
      <MobileSlideDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteReason("");
          setDeleteReasonError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#003d34",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Delete account</span>
          <IconButton
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteReason("");
              setDeleteReasonError("");
            }}
            sx={{ color: "#6b7280" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: "auto" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This action can't be
            undone.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12.2px",
              pt: "5.8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#0a0a0a",
                lineHeight: "14px",
              }}
            >
              Reason (required)
            </Typography>
            <TextField
              multiline
              rows={5}
              value={deleteReason}
              onChange={(e) => {
                setDeleteReason(e.target.value);
                if (e.target.value.trim()) {
                  setDeleteReasonError("");
                }
              }}
              onBlur={() => {
                if (!deleteReason.trim()) {
                  setDeleteReasonError("Reason is required");
                }
              }}
              placeholder="Please tell us your reason..."
              variant="outlined"
              fullWidth
              error={!!deleteReasonError}
              helperText={deleteReasonError}
            />
          </Box>
          <Typography variant="body2" sx={{ color: "#757575", mt: 2 }}>
            All your data, including your profile information, budget details,
            and documents will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: "1px solid #e4e7eb" }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteReason("");
              setDeleteReasonError("");
            }}
            sx={{
              color: "#374151",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!deleteReason.trim()) {
                setDeleteReasonError("Reason is required");
                addAlert({
                  severity: "error",
                  message: "Please provide a reason",
                  autoHide: true,
                  duration: 2000,
                });
                return;
              }
              if (deleteAccountRequestInFlightRef.current) {
                return;
              }
              try {
                deleteAccountRequestInFlightRef.current = true;
                setIsDeleting(true);
                const resp = await SupportService.requestAccountDeletion(
                  deleteReason,
                  { skipErrorCapture: false },
                  {
                    ...(typeof user?.userId === "number"
                      ? { userId: user.userId }
                      : {}),
                    ...(typeof customerData?.customerId === "number"
                      ? { customerId: customerData.customerId }
                      : {}),
                  }
                );
                if (resp.success) {
                  const requestId = (resp as any)?.data?.id;
                  const msg = requestId
                    ? `Your deletion request has been submitted (Ref #${requestId}). It will be actioned within 72 hours.`
                    : "Your deletion request has been sent. It will be actioned within 72 hours.";
                  addAlert({
                    severity: "success",
                    message: msg,
                    autoHide: true,
                    duration: 5000,
                  });
                  setDeleteDialogOpen(false);
                  setDeleteReason("");
                  setDeleteReasonError("");
                  setDeleteRequestId(requestId || null);
                  setDeleteSuccessOpen(true);
                } else {
                  const msg =
                    typeof resp.error === "string"
                      ? resp.error
                      : "Failed to submit deletion request";
                  addAlert({
                    severity: "error",
                    message: msg,
                    autoHide: true,
                    duration: 3000,
                  });
                }
              } finally {
                setIsDeleting(false);
                deleteAccountRequestInFlightRef.current = false;
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: "#ff0000",
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#cc0000" },
            }}
            disabled={isDeleting}
          >
            {isDeleting ? "Sending..." : "Delete account"}
          </Button>
        </DialogActions>
      </MobileSlideDialog>

      {/* Delete Request Success Dialog */}
      <MobileSlideDialog
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SuccessIcon sx={{ color: "#16a34a" }} />
            Request submitted
          </Box>
          <IconButton
            onClick={() => setDeleteSuccessOpen(false)}
            sx={{ color: "#6b7280" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: "auto" }}>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Your account deletion request has been received and will be actioned
            within 72 hours.
          </Typography>
          {deleteRequestId ? (
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Reference ID: <strong>#{deleteRequestId}</strong>
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: "1px solid #e4e7eb" }}>
          <Button
            onClick={() => setDeleteSuccessOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#1a5053",
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#153f41" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </MobileSlideDialog>
      {/* Mobile View */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          height: "100dvh",
          width: "100vw",
          backgroundColor: "#FFFFFF",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <Header />

        {/* Mobile Content */}
        <Box sx={{ flex: 1, p: 2, pt: 8, pb: 10 }}>
          {isLoading ? (
            <>
              <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} sx={{ mb: 4 }} />
            </>
          ) : (
            <>
              <Typography
                data-private
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#003d34",
                  mb: 1,
                  fontSize: "32px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                Hi, {greetingName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#757575", mb: 4, fontSize: "16px" }}
              >
                Review your details below. Tap the pencil to make changes.
              </Typography>
            </>
          )}

          {/* Personal Details Card */}
          <Paper
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#003d34",
                mb: 3,
                pb: 2,
                borderBottom: "1px solid #e4e7eb",
                fontSize: "20px",
              }}
            >
              {isLoading ? (
                <Skeleton variant="text" width="60%" />
              ) : (
                personalDetails.title
              )}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {isLoading
                ? Array.from({ length: 7 }).map((_, index) => (
                    <Box key={index}>{renderSkeletonField(true)}</Box>
                  ))
                : personalDetails.fields.map((field, index) =>
                    renderField(field, index, true)
                  )}
            </Box>
          </Paper>

          {/* Partner Details Card */}
          <Paper
            data-private
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e4e7eb",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#003d34",
                  fontSize: "20px",
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width="60%" />
                ) : (
                  partnerDetails.title
                )}
              </Typography>
              {!isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                  }}
                  onClick={() => setPartnerDialogOpen(true)}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#111726" }}
                  >
                    Edit
                  </Typography>
                  <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {isLoading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <Box key={index}>{renderSkeletonField(true)}</Box>
                  ))
                : partnerDetails.fields.map((field, index) =>
                    renderField(field, index, true)
                  )}
            </Box>
          </Paper>

          {/* Household Card */}
          <Paper
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e4e7eb",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#003d34",
                  fontSize: "20px",
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width="100%" />
                ) : (
                  childrenAndAdults.title
                )}
              </Typography>
              {!isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                  }}
                  onClick={() => handleDependentsDialogOpen()}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#111726" }}
                  >
                    Edit
                  </Typography>
                  <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <Box key={index}>{renderSkeletonField(true)}</Box>
                  ))
                : childrenAndAdults.fields.map((field, index) =>
                    renderField(field, index, true, false)
                  )}
            </Box>
          </Paper>

          {/* Other Circumstances Card — hidden while VULNERABILITY_ENABLED is false */}
          {VULNERABILITY_ENABLED && (
          <Paper
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e4e7eb",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#003d34",
                  fontSize: "20px",
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width="70%" />
                ) : (
                  "Other circumstances"
                )}
              </Typography>
              {!isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                  }}
                  onClick={() => setVulnerabilityDialogOpen(true)}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#111726" }}
                  >
                    Edit
                  </Typography>
                  <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                </Box>
              )}
            </Box>

            {isLoading ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1 }}>
                    <Skeleton variant="text" width="40%" height={24} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={48}
                    sx={{ borderRadius: "8px" }}
                  />
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={48}
                    sx={{ borderRadius: "8px", mt: 1 }}
                  />
                </Box>
                <Box>
                  <Skeleton
                    variant="text"
                    width="30%"
                    height={24}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
              </>
            ) : otherCircumstances.explicitlyNo ? (
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#757575", fontSize: "16px" }}
              >
                No
              </Typography>
            ) : otherCircumstances.skipped ? (
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#757575", fontSize: "16px" }}
              >
                None
              </Typography>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "16px",
                      }}
                    >
                      Categories chosen
                    </Typography>
                    <EditIcon onClick={() => setVulnerabilityDialogOpen(true)} sx={{ fontSize: 14, color: "#6b7280" }} />
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {otherCircumstances.categoriesChosen.length > 0 ? (
                      otherCircumstances.categoriesChosen.map(
                        (category, index) => (
                          <Box
                            key={index}
                            sx={{
                              backgroundColor: "rgba(0, 61, 52, 0.05)",
                              borderRadius: "8px",
                              px: 1.5,
                              py: 1.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                              width: "100%",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#003d34",
                                fontWeight: 500,
                                fontSize: "14px",
                              }}
                            >
                              {category}
                            </Typography>
                            <InfoIcon sx={{ fontSize: 16, color: "#003d34" }} />
                          </Box>
                        )
                      )
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "#757575", fontSize: "14px" }}
                      >
                        None selected
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#374151",
                      mb: 1,
                      fontSize: "16px",
                    }}
                  >
                    Consent
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#757575", fontSize: "14px", lineHeight: 1.6 }}
                  >
                    {otherCircumstances.consent || "Not provided"}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
          )}

          {/* Property and Possessions Card */}
          <Paper
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e4e7eb",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#003d34",
                  fontSize: "18px",
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width="70%" />
                ) : (
                  propertyAndPossessions.title
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {isLoading ? (
                <>
                  <Skeleton variant="text" width="60%" height={18} />
                  <Skeleton variant="text" width="70%" height={20} />
                </>
              ) : (
                propertyAndPossessions.fields.map((field, index) => (
                  <Box key={index}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#757575", fontSize: "12px" }}
                    >
                      {field.label}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#111726",
                          fontSize: "16px",
                        }}
                      >
                        {field.value}
                      </Typography>
                      {field.editable && (
                        <IconButton
                          size="small"
                          sx={{ color: "#6b7280", p: 0 }}
                          onClick={() => handleEdit(field.label)}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Paper>

          {/* Referred Company Card */}
          <Paper
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #e4e7eb",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#003d34",
                  fontSize: "20px",
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width="60%" />
                ) : (
                  "Referred company"
                )}
              </Typography>
              {/* Hide Edit button for creditor referrals - company info is provided by creditor and should not be editable */}
              {!isLoading && !referredCompany?.isCreditorReferral && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                  }}
                  onClick={() => setCompanyDialogOpen(true)}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#111726" }}
                  >
                    Edit
                  </Typography>
                  <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                </Box>
              )}
            </Box>

            {isLoading ? (
              <>
                <Skeleton
                  variant="text"
                  width="80%"
                  height={28}
                  sx={{ mb: 1 }}
                />
                <Skeleton variant="text" width="40%" height={16} />
              </>
            ) : referredCompany ? (
              referredCompany.isNotReferred ? (
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: "#111726", fontSize: "16px" }}
                >
                  {referredCompany.companyName}
                </Typography>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {referredCompany.shouldShowCompanyName && (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#111726",
                        fontSize: "16px",
                      }}
                    >
                      {referredCompany.companyName}
                    </Typography>
                  )}

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "#757575", fontSize: "14px" }}
                      >
                        How you heard about us
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#111726",
                          fontSize: "16px",
                        }}
                      >
                        {referredCompany.referralMethod}
                      </Typography>
                    </Box>

                    {referredCompany.shouldShowProduct && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "#757575", fontSize: "14px" }}
                        >
                          Product Name
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#111726",
                            fontSize: "16px",
                          }}
                        >
                          {referredCompany.productName}
                        </Typography>
                      </Box>
                    )}

                    {referredCompany.shouldShowOtherCompanyName && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "#757575", fontSize: "14px" }}
                        >
                          Other company name
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#111726",
                            fontSize: "16px",
                          }}
                        >
                          {referredCompany.otherCompanyName}
                        </Typography>
                      </Box>
                    )}

                    {referredCompany.shouldShowAccountType && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "#757575", fontSize: "14px" }}
                        >
                          Account Type
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#111726",
                            fontSize: "16px",
                          }}
                        >
                          {accountTypeDisplay || referredCompany.accountType}
                        </Typography>
                      </Box>
                    )}

                    {referredCompany.shouldShowReference && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "#757575", fontSize: "14px" }}
                        >
                          Reference Number
                        </Typography>
                        <Typography
                          data-private
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#111726",
                            fontSize: "16px",
                          }}
                        >
                          {referenceNumberDisplay}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )
            ) : null}
          </Paper>

          {/* Marketing Services Card */}
          <Paper
            sx={{
              mb: 3,
              p: 3,
              border: "1px solid #e4e7eb",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            {isLoading ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton
                    variant="rectangular"
                    width={48}
                    height={24}
                    sx={{ borderRadius: "12px" }}
                  />
                </Box>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={60}
                  sx={{ borderRadius: "12px" }}
                />
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MarketingSpeakerIcon size={18} color="#000" />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: "15px", color: "#000" }}
                    >
                      Marketing services
                    </Typography>
                  </Box>
                  <Switch
                    checked={marketingConsent}
                    onChange={async (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const checked = e.target.checked;
                      // Switch "on" represents the customer being OPTED IN to marketing communications.
                      setMarketingConsent(checked);
                      try {
                        const ids = await resolveRecordIds();
                        if (!ids.customerId || !user?.userId) return;
                        if (!marketingRecord?.id) {
                          const createResp = await MarketingService.create({
                            customerId: ids.customerId,
                            userId: Number(user.userId),
                            optedOut: checked ? "N" : "Y",
                            source: "IEHUB",
                          });
                          const rec: any =
                            (createResp as any)?.data || createResp;
                          if (rec) {
                            setMarketingRecord({
                              id: rec.id,
                              lastUpdateDate: rec.lastUpdateDate,
                            });
                            addAlert({
                              severity: "success",
                              message: "Marketing preference saved",
                              autoHide: true,
                              duration: 1500,
                            });
                          }
                        } else {
                          const updatePayload: any = {
                            id: marketingRecord.id as number,
                            customerId: ids.customerId,
                            userId: Number(user.userId),
                            optedOut: checked ? "N" : "Y",
                            source: "IEHUB",
                          };
                          if (marketingRecord.lastUpdateDate) {
                            updatePayload.lastUpdateDate =
                              marketingRecord.lastUpdateDate;
                          }
                          const updateResp =
                            await MarketingService.update(updatePayload);
                          const rec: any =
                            (updateResp as any)?.data || updateResp;
                          if (rec) {
                            setMarketingRecord({
                              id: rec.id,
                              lastUpdateDate: rec.lastUpdateDate,
                            });
                            addAlert({
                              severity: "success",
                              message: "Marketing preference updated",
                              autoHide: true,
                              duration: 1500,
                            });
                          }
                        }
                      } catch {
                        addAlert({
                          severity: "error",
                          message: "Failed to update marketing preference",
                          autoHide: true,
                          duration: 2000,
                        });
                      }
                    }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#4caf50",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#4caf50",
                        },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    backgroundColor: "#e8efee",
                    border: "1px solid #1a5053",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <InfoIcon
                    sx={{ fontSize: 16, color: "#00243e", flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#00243e",
                      lineHeight: 1.5,
                    }}
                  >
                    By opting in, we may send you marketing emails about new
                    services we can offer you.
                  </Typography>
                </Box>
              </>
            )}
          </Paper>

          {/* Delete Account Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              pt: 2,
              pb: 4,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DeleteAccountIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                fontWeight: 600,
                fontStyle: "Semi Bold",
                fontSize: "15px",
                leadingTrim: "NONE",
                lineHeight: "22.77px",
                letterSpacing: "-0.3px",
                verticalAlign: "middle",
                backgroundColor: "#ffffff",
                color: "#ff0000",
                textTransform: "none",
                borderRadius: "12px",
                px: 2,
                py: 1.5,
                "&:hover": { backgroundColor: "#cc0000" },
              }}
            >
              Delete my account
            </Button>
          </Box>

          {/* Static Bottom Footer */}
          <Divider sx={{ mb: 2}} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              maxWidth: "lg",
              margin: "0 auto",
              gap: { xs: 2, md: 0 },
              pb: { xs: 5, md: 6 },
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/dashboard")}
              fullWidth={isMobile}
              sx={{
                flex:1,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "16px",
                py: 1.5,
                px: 4,
                borderColor: "#E0E0E0",
                color: "#374151",
                order: { xs: 1, md: 1 },
                mt: { xs: 0, md: 0 },
                "&:hover": {
                  borderColor: "#BDBDBD",
                  backgroundColor: "#F5F5F5",
                },
              }}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              onClick={() => navigate("/budget")}
              fullWidth={isMobile}
              sx={{
                flex:2,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "16px",
                py: 1.5,
                px: { xs: 0, md: 4 },
                backgroundColor: "#c3f53c",
                color: "#003d34",
                order: { xs: 2, md: 2 },
                mb: { xs: 0, md: 0 },
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#b4e62d",
                  boxShadow: "none",
                },
              }}
            >
              Continue
            </Button>
          </Box>
        </Box>

        {/* Bottom Tab Bar for Mobile */}
        <BottomTabBar />
      </Box>

      {/* Desktop View */}
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Header />

        {/* Desktop Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            py: 4,
            px: 2,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "1280px", px: 4 }}>
            {/* Page Title */}
            <Box sx={{ mb: 5 }}>
              {isLoading ? (
                <>
                  <Skeleton
                    variant="text"
                    width="30%"
                    height={50}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="70%" height={28} />
                </>
              ) : (
                <>
                  <Typography
                    data-private
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#003d34",
                      mb: 1,
                      fontSize: "36px",
                    }}
                  >
                    Hi, {greetingName}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#757575", fontSize: "18px" }}
                  >
                    Have a read over your details below and click on the Pencil icon to edit if you need to change something..
                  </Typography>
                </>
              )}
            </Box>

            {/* Grid Layout with independent columns */}
            <Box
              sx={{
                display: "grid",
                // IMPORTANT: use minmax(0, fr) so grid items can shrink below
                // min-content size. Without this, long/unbreakable content in the left
                // column can overflow into the right column and "steal" clicks.
                gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
                gap: 3,
                mb: 3,
                // Defensive: prevent any horizontal overflow from one column
                // overlapping the other column and intercepting pointer events.
                overflowX: "hidden",
              }}
            >
              {/* Left Column */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  minWidth: 0,
                }}
              >
                {/* Personal Details Card */}
                <Paper
                  sx={{
                    p: 4,
                    border: "1px solid #e4e7eb",
                    borderRadius: "16px",
                    boxShadow: "none",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#003d34",
                      mb: 3,
                      pb: 2,
                      borderBottom: "1px solid #e4e7eb",
                      fontSize: "20px",
                    }}
                  >
                    {isLoading ? (
                      <Skeleton variant="text" width="60%" />
                    ) : (
                      personalDetails.title
                    )}
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    {isLoading ? (
                      <>
                        {/* Row 1: Name and Date of Birth */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {renderSkeletonField()}
                          {renderSkeletonField()}
                        </Box>

                        {/* Row 2: Email and Phone */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {renderSkeletonField()}
                          {renderSkeletonField()}
                        </Box>

                        {/* Row 3: Address and Landline Number */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {renderSkeletonField()}
                          {renderSkeletonField()}
                        </Box>

                        {/* Row 4: Employment Status - Full Width */}
                        {renderSkeletonField(true)}
                      </>
                    ) : (
                      <>
                        {/* Row 1: Name and Date of Birth */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {personalDetails.fields[0] &&
                            renderField(personalDetails.fields[0], 0)}
                          {personalDetails.fields[1] &&
                            renderField(personalDetails.fields[1], 1)}
                        </Box>

                        {/* Row 2: Email and Phone */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {personalDetails.fields[2] &&
                            renderField(personalDetails.fields[2], 2)}
                          {personalDetails.fields[3] &&
                            renderField(personalDetails.fields[3], 3)}
                        </Box>

                        {/* Row 3: Address and Landline Number */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {personalDetails.fields[4] &&
                            renderField(personalDetails.fields[4], 4)}
                          {personalDetails.fields[5] &&
                            renderField(personalDetails.fields[5], 5)}
                        </Box>

                        {/* Row 4: Employment Status - Full Width */}
                        {personalDetails.fields[6] &&
                          renderField(personalDetails.fields[6], 6, true)}
                      </>
                    )}
                  </Box>
                </Paper>

                {/* Partner Details and Vehicles Row */}
                <Box sx={{ display: "flex", gap: 3 }}>
                  {/* Partner Details Card - 60% width */}
                  <Paper
                    data-private
                    sx={{
                      flex: "3",
                      p: 3,
                      border: "1px solid #e4e7eb",
                      borderRadius: "16px",
                      boxShadow: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid #e4e7eb",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#003d34",
                          fontSize: "18px",
                        }}
                      >
                        {isLoading ? (
                          <Skeleton variant="text" width="60%" />
                        ) : (
                          partnerDetails.title
                        )}
                      </Typography>
                      {!isLoading && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                          }}
                          onClick={() => setPartnerDialogOpen(true)}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#111726",
                            }}
                          >
                            Edit
                          </Typography>
                          <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                        </Box>
                      )}
                    </Box>
                    {/* 2x2 Grid Layout */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                        rowGap: 2,
                      }}
                    >
                      {isLoading
                        ? Array.from({ length: 4 }).map((_, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Skeleton
                                variant="text"
                                width="50%"
                                height={18}
                              />
                              <Skeleton
                                variant="text"
                                width="70%"
                                height={20}
                              />
                            </Box>
                          ))
                        : partnerDetails.fields.map((field, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#757575", fontSize: "12px" }}
                              >
                                {field.label}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111726",
                                  fontSize: "14px",
                                }}
                              >
                                {field.value}
                              </Typography>
                            </Box>
                          ))}
                    </Box>
                  </Paper>

                  {/* Household Card - 40% width */}
                  <Paper
                    sx={{
                      flex: "2",
                      p: 3,
                      border: "1px solid #e4e7eb",
                      borderRadius: "16px",
                      boxShadow: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid #e4e7eb",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#003d34",
                          fontSize: "18px",
                        }}
                      >
                        {isLoading ? (
                          <Skeleton variant="text" width="70%" />
                        ) : (
                          childrenAndAdults.title
                        )}
                      </Typography>
                      {!isLoading && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                          }}
                          onClick={() => handleDependentsDialogOpen()}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#111726",
                            }}
                          >
                            Edit
                          </Typography>
                          <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {isLoading
                        ? Array.from({ length: 3 }).map((_, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Skeleton
                                variant="text"
                                width="60%"
                                height={18}
                              />
                              <Skeleton
                                variant="text"
                                width="40%"
                                height={20}
                              />
                            </Box>
                          ))
                        : childrenAndAdults.fields.map((field, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#757575", fontSize: "12px" }}
                              >
                                {field.label}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111726",
                                  fontSize: "14px",
                                }}
                              >
                                {field.value}
                              </Typography>
                            </Box>
                          ))}
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Right Column - Independent */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  minWidth: 0,
                }}
              >
                {/* Other Circumstances Card — hidden while VULNERABILITY_ENABLED is false */}
                {VULNERABILITY_ENABLED && (
                <Paper
                  sx={{
                    flex: 1,
                    p: 4,
                    border: "1px solid #e4e7eb",
                    borderRadius: "16px",
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                      pb: 2,
                      borderBottom: "1px solid #e4e7eb",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#003d34",
                        fontSize: "20px",
                      }}
                    >
                      {isLoading ? (
                        <Skeleton variant="text" width="40%" />
                      ) : (
                        "Other circumstances"
                      )}
                    </Typography>
                    {!isLoading && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                        }}
                        onClick={() => setVulnerabilityDialogOpen(true)}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#111726",
                          }}
                        >
                          Edit
                        </Typography>
                        <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                      </Box>
                    )}
                  </Box>

                  {isLoading ? (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ mb: 1 }}>
                          <Skeleton variant="text" width="30%" height={24} />
                        </Box>
                        <Skeleton
                          variant="rounded"
                          width="100%"
                          height={48}
                          sx={{ borderRadius: "8px" }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="100%"
                          height={48}
                          sx={{ borderRadius: "8px", mt: 1 }}
                        />
                      </Box>
                      <Box>
                        <Skeleton
                          variant="text"
                          width="20%"
                          height={24}
                          sx={{ mb: 1 }}
                        />
                        <Skeleton variant="text" width="100%" height={20} />
                        <Skeleton variant="text" width="95%" height={20} />
                        <Skeleton variant="text" width="85%" height={20} />
                      </Box>
                    </>
                  ) : otherCircumstances.explicitlyNo ? (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#757575",
                        fontSize: "16px",
                      }}
                    >
                      No
                    </Typography>
                  ) : otherCircumstances.skipped ? (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#757575",
                        fontSize: "16px",
                      }}
                    >
                      None
                    </Typography>
                  ) : (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "16px",
                            }}
                          >
                            Categories chosen
                          </Typography>
                           <EditIcon onClick={() => setVulnerabilityDialogOpen(true)} sx={{ fontSize: 14, color: "#6b7280" }} />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {otherCircumstances.categoriesChosen.length > 0 ? (
                            otherCircumstances.categoriesChosen.map(
                              (category, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    backgroundColor: "rgba(0, 61, 52, 0.05)",
                                    borderRadius: "8px",
                                    px: 1.5,
                                    py: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    width: "100%",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#003d34",
                                      fontWeight: 500,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {category}
                                  </Typography>
                                  <InfoIcon
                                    sx={{ fontSize: 16, color: "#003d34" }}
                                  />
                                </Box>
                              )
                            )
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: "#757575", fontSize: "14px" }}
                            >
                              None selected
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            mb: 1,
                            fontSize: "16px",
                          }}
                        >
                          Consent
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#757575",
                            fontSize: "14px",
                            lineHeight: 1.6,
                          }}
                        >
                          {otherCircumstances.consent || "Not provided"}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Paper>
                )}

                {/* Property and Possessions Card */}
                <Paper
                  sx={{
                    p: 3,
                    border: "1px solid #e4e7eb",
                    borderRadius: "16px",
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 2,
                      borderBottom: "1px solid #e4e7eb",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#003d34",
                        fontSize: "18px",
                      }}
                    >
                      {isLoading ? (
                        <Skeleton variant="text" width="70%" />
                      ) : (
                        propertyAndPossessions.title
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {isLoading ? (
                      <>
                        <Skeleton variant="text" width="60%" height={18} />
                        <Skeleton variant="text" width="70%" height={20} />
                      </>
                    ) : (
                      propertyAndPossessions.fields.map((field, index) => (
                        <Box key={index}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#757575", fontSize: "12px" }}
                          >
                            {field.label}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "#111726",
                                fontSize: "16px",
                              }}
                            >
                              {field.value}
                            </Typography>
                            {field.editable && (
                              <IconButton
                                size="small"
                                sx={{ color: "#6b7280", p: 0 }}
                                onClick={() => handleEdit(field.label)}
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                </Paper>

                {/* Referred Company Card */}
                <Paper
                  sx={{
                    p: 3,
                    border: "1px solid #e4e7eb",
                    borderRadius: "16px",
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 2,
                      borderBottom: "1px solid #e4e7eb",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#003d34",
                        fontSize: "18px",
                      }}
                    >
                      {isLoading ? (
                        <Skeleton variant="text" width="70%" />
                      ) : (
                        "Referred company"
                      )}
                    </Typography>
                    {/* Hide Edit button for creditor referrals - company info is provided by creditor and should not be editable */}
                    {!isLoading && !referredCompany?.isCreditorReferral && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                        }}
                        onClick={() => setCompanyDialogOpen(true)}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#111726",
                          }}
                        >
                          Edit
                        </Typography>
                        <EditIcon sx={{ fontSize: 10.5, color: "#6b7280" }} />
                      </Box>
                    )}
                  </Box>
                  {isLoading ? (
                    <>
                      <Skeleton
                        variant="text"
                        width="70%"
                        height={24}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width="40%" height={16} />
                    </>
                  ) : referredCompany ? (
                    referredCompany.isNotReferred ? (
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#111726",
                          fontSize: "16px",
                        }}
                      >
                        {referredCompany.companyName}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {referredCompany.shouldShowCompanyName && (
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#111726",
                              fontSize: "16px",
                            }}
                          >
                            {referredCompany.companyName}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#757575", fontSize: "12px" }}
                            >
                              How you heard about us
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "#111726",
                                fontSize: "14px",
                              }}
                            >
                              {referredCompany.referralMethod}
                            </Typography>
                          </Box>

                          {referredCompany.shouldShowProduct && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#757575", fontSize: "12px" }}
                              >
                                Product Name
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111726",
                                  fontSize: "14px",
                                }}
                              >
                                {referredCompany.productName}
                              </Typography>
                            </Box>
                          )}

                          {referredCompany.shouldShowAccountType && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#757575", fontSize: "12px" }}
                              >
                                Account Type
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111726",
                                  fontSize: "14px",
                                }}
                              >
                                {accountTypeDisplay ||
                                  referredCompany.accountType}
                              </Typography>
                            </Box>
                          )}

                          {referredCompany.shouldShowOtherCompanyName && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#757575", fontSize: "12px" }}
                              >
                                Other company name
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111726",
                                  fontSize: "14px",
                                }}
                              >
                                {referredCompany.otherCompanyName}
                              </Typography>
                            </Box>
                          )}

                          {referredCompany.shouldShowReference && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#757575", fontSize: "12px" }}
                              >
                                Reference number
                              </Typography>
                              <Typography
                                data-private
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111726",
                                  fontSize: "14px",
                                }}
                              >
                                {referenceNumberDisplay}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  ) : null}
                </Paper>

                {/* Marketing Services Card */}
                <Paper
                  sx={{
                    width: "389px",
                    p: 3,
                    border: "1px solid #e4e7eb",
                    borderRadius: "16px",
                    boxShadow: "none",
                    alignSelf: "flex-start",
                  }}
                >
                  {isLoading ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton
                          variant="rectangular"
                          width={48}
                          height={24}
                          sx={{ borderRadius: "12px" }}
                        />
                      </Box>
                      <Skeleton
                        variant="rounded"
                        width="100%"
                        height={60}
                        sx={{ borderRadius: "12px" }}
                      />
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <MarketingSpeakerIcon size={18} color="#000" />
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              fontSize: "15px",
                              color: "#000",
                            }}
                          >
                            Marketing services
                          </Typography>
                        </Box>
                        <Switch
                          checked={marketingConsent}
                          onChange={async (
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const checked = e.target.checked;
                            // Switch "on" represents the customer being OPTED IN to marketing communications.
                            setMarketingConsent(checked);
                            try {
                              const ids = await resolveRecordIds();
                              if (!ids.customerId || !user?.userId) return;
                              if (!marketingRecord?.id) {
                                const createResp =
                                  await MarketingService.create({
                                    customerId: ids.customerId,
                                    userId: Number(user.userId),
                                    optedOut: checked ? "N" : "Y",
                                    source: "IEHUB",
                                  });
                                const rec: any =
                                  (createResp as any)?.data || createResp;
                                if (rec) {
                                  setMarketingRecord({
                                    id: rec.id,
                                    lastUpdateDate: rec.lastUpdateDate,
                                  });
                                  addAlert({
                                    severity: "success",
                                    message: "Marketing preference saved",
                                    autoHide: true,
                                    duration: 1500,
                                  });
                                }
                              } else {
                                const updatePayload: any = {
                                  id: marketingRecord.id as number,
                                  customerId: ids.customerId,
                                  userId: Number(user.userId),
                                  optedOut: checked ? "N" : "Y",
                                  source: "IEHUB",
                                };
                                if (marketingRecord.lastUpdateDate) {
                                  updatePayload.lastUpdateDate =
                                    marketingRecord.lastUpdateDate;
                                }
                                const updateResp =
                                  await MarketingService.update(updatePayload);
                                const rec: any =
                                  (updateResp as any)?.data || updateResp;
                                if (rec) {
                                  setMarketingRecord({
                                    id: rec.id,
                                    lastUpdateDate: rec.lastUpdateDate,
                                  });
                                  addAlert({
                                    severity: "success",
                                    message: "Marketing preference updated",
                                    autoHide: true,
                                    duration: 1500,
                                  });
                                }
                              }
                            } catch {
                              addAlert({
                                severity: "error",
                                message:
                                  "Failed to update marketing preference",
                                autoHide: true,
                                duration: 2000,
                              });
                            }
                          }}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#4caf50",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#4caf50",
                              },
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: "#e8efee",
                          border: "1px solid #1a5053",
                          borderRadius: "12px",
                          p: 1.5,
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <InfoIcon
                          sx={{ fontSize: 16, color: "#00243e", flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#00243e",
                            lineHeight: 1.5,
                          }}
                        >
                          By opting in, we may send you marketing emails about
                          new services we can offer you.
                        </Typography>
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            </Box>

            {/* Other Circumstances and Marketing Services Row */}
            <Box sx={{ display: "flex", gap: 3, mb: 3 }}></Box>

            {/* Delete Account Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pt: 2,
                mb: 5,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<DeleteAccountIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  fontWeight: 600,
                  fontStyle: "Semi Bold",
                  fontSize: "15px",
                  leadingTrim: "NONE",
                  lineHeight: "22.77px",
                  letterSpacing: "-0.3px",
                  verticalAlign: "middle",
                  backgroundColor: "#ffffff",
                  color: "#ff0000",
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 2,
                  py: 1.5,
                  "&:hover": { backgroundColor: "#fefefe" },
                }}
              >
                Delete my account
              </Button>
            </Box>

            {/* Static Bottom Footer */}
            <Divider sx={{ mb: 2.5 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", md: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                maxWidth: "lg",
                margin: "0 auto",
                gap: { xs: 2, md: 0 },
                pb: { xs: 12, md: 6 },
                width: "100%",
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/dashboard")}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "16px",
                  py: 1.5,
                  px: 4,
                  borderColor: "#E0E0E0",
                  color: "#374151",
                  order: { xs: 1, md: 1 },
                  mt: { xs: 0, md: 0 },
                  maxWidth: { xs: "100%", md: "200px" },
                  "&:hover": {
                    borderColor: "#BDBDBD",
                    backgroundColor: "#F5F5F5",
                  },
                }}
              >
                Back
              </Button>
              
              <Button
                variant="contained"
                onClick={() => navigate("/budget")}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "16px",
                  py: 1.5,
                  px: { xs: 0, md: 4 },
                  backgroundColor: "#c3f53c",
                  color: "#003d34",
                  order: { xs: 2, md: 2 },
                  mb: { xs: 0, md: 0 },
                  maxWidth: { xs: "100%", md: "300px" },
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#b4e62d",
                    boxShadow: "none",
                  },
                }}
              >
                Continue to budget
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ProfilePage;
